import React, { useState, useCallback, useEffect } from 'react'
import HoverTooltip from '@/Components/HoverTooltip'
import SeasonRolloverControl from '@/Views/Admin/SeasonRolloverControl'
import AdminRulesSection from '@/Views/Admin/AdminRulesSection'
import { useAuth } from '@/contexts/AuthContext'

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api'

const POSITIONS = ['QB', 'RB', 'WR', 'TE', 'K']
const TIERS = ['Random', 'S', 'A', 'B', 'C', 'D']

const TIER_COLORS: Record<string, string> = {
  S: '#f59e0b',
  A: '#22c55e',
  B: '#3b82f6',
  C: '#94a3b8',
  D: '#ef4444',
}

const TIER_LABELS: Record<string, string> = {
  S: 'S (93+)',
  A: 'A (87–92)',
  B: 'B (77–86)',
  C: 'C (69–76)',
  D: 'D (<69)',
  Random: 'Random',
}

interface CreatedPlayer {
  name: string
  position: string
  rating: number
  tier: string
}

interface MonitorData {
  deploySafety: { safe: boolean; reason: string }
  simulation: { isActive: boolean; phase: string; lastSaved: string | null }
  season: {
    seasonNumber: number; currentWeek: number; currentWeekText: string | null
    inPlayoffs: boolean; playoffRound: string | null; isComplete: boolean
    champion: string | null; mvp: string | null
    totalGames: number; completedGames: number
  }
  liveGames: { active: number; scheduled: number; final: number }
  timing: { mode: string | null; catchingUp: boolean }
  counts: { teams: number; activePlayers: number; freeAgents: number; retiredPlayers: number; hallOfFame: number }
  websockets: { total_connections: number; unique_users: number; channels: Record<string, number> }
  personality?: {
    total: number
    baseVibes: number
    commonVariants: number
    rareVariants: number
    unassigned: number
    quirked: number
    rareVariantList: string[]
    personalityCounts: Record<string, number>
    quirkCounts: Record<string, number>
  }
}

interface AnalyticsData {
  seasonNumber: number
  economy: {
    totalCirculation: number; totalEarned: number; totalSpent: number
    earningsBreakdown: Record<string, number>; spendingBreakdown: Record<string, number>
    seasonEarnings: number; seasonSpending: number
    avgBalance?: number; medianBalance?: number
    avgWeeklyPayout?: number; maxWeeklyPayout?: number; weeklyPayoutRecipients?: number; lastPayoutWeek?: number | null
    richestUsers?: { username: string; balance: number }[]
  }
  cards: {
    totalCards: number; byEdition: Record<string, number>; bySource: Record<string, number>
    topEffects: { effectName: string; count: number; tooltip?: string }[]
    bottomEffects?: { effectName: string; count: number; tooltip?: string }[]
    packOpenings: Record<string, number>
    combineUsage?: Record<string, number>; totalCombineUses?: number
    usersWhoEquipped?: number
  }
  fantasy: {
    totalRosters: number; avgTotalPoints: number; avgCardBonus: number
    totalSwapsUsed: number; totalPurchasedSwaps: number
    topRosteredPlayers: { name: string; count: number }[]
  }
  users: {
    totalUsers: number; active7d: number; active30d: number
    onboardingRate: number; onboardedCount: number
    favoriteTeams: { team: string; count: number }[]
    adoption: { fantasy: number; cards: number; pickEm: number; funding: number }
    churnRiskCount?: number
    dailyActiveUsers?: { date: string; count: number }[]
    onboardingFunnel?: {
      hasAccount: number; pickedUsername: number; choseFavTeam: number
      draftedRoster: number; hasCards: number
    }
  }
  funding: {
    totalFanContributions: number
    tierDistribution: Record<string, number>
    topTeams: { team: string; contributions: number; tier: string }[]
  }
  pickEm: {
    totalPicks: number; accuracy: number; participants: number
    trend?: { week: number; participants: number; picks: number }[]
  }
}

const sectionStyle: React.CSSProperties = {
  backgroundColor: '#1e293b',
  borderRadius: '8px',
  padding: '24px',
  marginBottom: '24px',
}

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: '600',
  color: '#94a3b8',
  letterSpacing: '0.02em',
  marginBottom: '6px',
  textTransform: 'uppercase',
}

const inputStyle: React.CSSProperties = {
  backgroundColor: '#0f172a',
  border: '1px solid #334155',
  borderRadius: '6px',
  color: '#e2e8f0',
  fontSize: '14px',
  padding: '8px 12px',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
}

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  cursor: 'pointer',
}

const btnStyle: React.CSSProperties = {
  backgroundColor: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  padding: '9px 18px',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
}

// ── Password gate ──────────────────────────────────────────────────────────

// Format an achievement target/progress number for the admin panel based on its key.
// Most families just append a unit ("1,000 FP"); Compound renders its encoded integer
// as a human multiplier ("1.20x").
const formatAchievementValue = (key: string, value: number): string => {
  if (!key) return value.toLocaleString()
  const base = key.replace(/_[ivx]+$/i, '')   // strip trailing tier: "banner_week_iii" → "banner_week"
  if (base === 'compound') {
    // Target stored as multiplier × 100 (e.g. 120 → "1.20x")
    return `${(value / 100).toFixed(2)}x`
  }
  const unitMap: Record<string, string> = {
    // Rookie goals (target=1): no unit
    rookie: '', prognosticator: '', pack_popper: '', field_general: '',
    deck_builder: '', patron: '',
    // Single-target season goals
    sharp: '', sparkler: '', perfect_week: '',
    // Metric-based season goals
    dedicated: 'weeks', veteran: 'weeks', curator: 'cards',
    tycoon: 'floobits', banner_week: 'FP', dynamo: 'FP', oracle: 'pts',
    magnate: 'floobits', racket: 'floobits',
    podium: 'finishes', pundit: 'finishes', benefactor: 'floobits',
  }
  const unit = unitMap[base] ?? ''
  return unit ? `${value.toLocaleString()} ${unit}` : value.toLocaleString()
}

const NAME_SOURCE_LABEL: Record<string, string> = {
  pool: 'in the pool', held: 'held for reuse', player: 'player', retired: 'retired player',
  coach: 'coach', suggested: 'Discord suggestion', batch: 'this batch',
}

const NameMatches: React.FC<{ matches: { name: string; source: string; score: number; sameLineage: boolean }[] }> =
  ({ matches }) => (
    <div style={{ fontSize: '12px', color: '#cbd5e1', marginTop: '3px', lineHeight: 1.6 }}>
      {matches.map(m => (
        <span key={`${m.source}-${m.name}`} style={{ marginRight: '12px', whiteSpace: 'nowrap' }}>
          {m.name}
          <span style={{ color: '#94a3b8' }}>
            {' '}({NAME_SOURCE_LABEL[m.source] ?? m.source}, {m.sameLineage ? 'same name' : `${Math.round(m.score * 100)}%`})
          </span>
        </span>
      ))}
    </div>
  )

const SESSION_KEY = 'floosball_admin_pw'

const PasswordGate: React.FC<{ onAuth: (pw: string) => void }> = ({ onAuth }) => {
  const [value, setValue] = useState('')
  const [error, setError] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Verify against the backend — if a request succeeds the password is correct
    try {
      const res = await fetch(`${API_BASE}/admin/names`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Password': value },
        body: JSON.stringify({ names: [] }),
      })
      // 400 = no names provided (auth passed), 403 = wrong password
      if (res.status === 403) {
        setError(true)
        return
      }
      sessionStorage.setItem(SESSION_KEY, value)
      onAuth(value)
    } catch {
      setError(true)
    }
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      backgroundColor: '#0f172a',
    }}>
      <form
        onSubmit={handleSubmit}
        style={{
          backgroundColor: '#1e293b',
          borderRadius: '10px',
          padding: '40px',
          width: '320px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#e2e8f0', margin: 0 }}>
          Admin Access
        </h2>
        <div>
          <div style={labelStyle}>Password</div>
          <input
            type="password"
            value={value}
            onChange={e => { setValue(e.target.value); setError(false) }}
            style={{
              ...inputStyle,
              borderColor: error ? '#ef4444' : '#334155',
            }}
            autoFocus
          />
          {error && (
            <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '6px' }}>
              Incorrect password.
            </div>
          )}
        </div>
        <button type="submit" style={btnStyle}>Enter</button>
      </form>
    </div>
  )
}

// ── Admin content ──────────────────────────────────────────────────────────

interface AdminUser {
  id: number
  email: string
  username: string | null
  floobits: number
  lifetimeEarned: number
  lifetimeSpent: number
  favoriteTeam: string | null
  favoriteTeamId: number | null
  onboarded: boolean
  createdAt: string | null
  isActive: boolean
  lastLoginAt: string | null
  isAdmin: boolean
}

interface EffectOption { name: string; displayName: string; edition: string }
interface CardOptions {
  editions: string[]
  effects: Record<string, EffectOption[]>
  classifications: string[]
  categories: string[]
}
interface PlayerSearchResult {
  id: number; name: string; position: string; positionNum: number
  rating: number; teamId: number; teamName: string
}

const EDITION_COLORS_MAP: Record<string, string> = {
  metallic: '#94a3b8', holographic: '#bae6fd',
  prismatic: '#f9a8d4', diamond: '#a5f3fc',
}

const CATEGORY_FOR_POSITION: Record<string, string> = {
  QB: 'multiplier', RB: 'floobits', WR: 'flat_fp', TE: 'conditional', K: 'streak',
}

const AdminContent: React.FC<{
  password: string | null
  getToken: (() => Promise<string | null>) | null
}> = ({ password, getToken }) => {
  const buildHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const h: Record<string, string> = { 'Content-Type': 'application/json' }
    if (getToken) {
      try {
        const t = await getToken()
        if (t) h['Authorization'] = `Bearer ${t}`
      } catch { /* fall through */ }
    }
    if (password) h['X-Admin-Password'] = password
    return h
  }, [getToken, password])

  // Name pool
  type NameMatch = { name: string; source: string; score: number; sameLineage: boolean }
  type NameCheck = { name: string; status: 'clear' | 'similar' | 'blocked'; matches: NameMatch[] }
  type AddResult = {
    added: number; total: number
    rejectedInUse?: string[]; rejectedInPool?: string[]; duplicatesInBatch?: string[]
  }
  const [namesInput, setNamesInput] = useState('')
  const [namesResult, setNamesResult] = useState<AddResult | null>(null)
  const [namesError, setNamesError] = useState<string | null>(null)
  const [namesLoading, setNamesLoading] = useState(false)
  // A typed batch is checked against every name in the league before it is added. Anything
  // close to an existing name waits here for a decision instead of going straight in.
  const [nameChecks, setNameChecks] = useState<NameCheck[] | null>(null)
  const [nameChoices, setNameChoices] = useState<Record<string, boolean>>({})
  // The pool itself, for browsing.
  const [poolNames, setPoolNames] = useState<string[]>([])
  const [poolHeld, setPoolHeld] = useState<{ name: string; availableSeason: number }[]>([])
  const [poolFilter, setPoolFilter] = useState('')
  const [poolError, setPoolError] = useState<string | null>(null)

  // ── League news announcements ─────────────────────────────────────────────
  type ConversationTurn = { core: string; text: string; turnIndex: number | null }
  type Announcement = {
    id: number; headline: string; body: string | null; pinned: boolean
    teamId: number | null; core: string | null
    season: number; week: number; createdAt: string | null
    // Present only on a Cores CONVERSATION. The list folds an exchange's turn rows into
    // one entry, so `id` is its first turn and `turns` is the whole thing in spoken order.
    exchangeId?: string; turns?: ConversationTurn[]
  }
  // Two different things, which is why they are two controls. "Post as" decides whether
  // the row is a written notice or a line in a Core's voice — a Core post threads with
  // the sim's own Cores lines and is prefixed with their name. "Icon" only applies to a
  // league notice, since a Core post already has one.
  const NEWS_CORES = ['cassian', 'pyre', 'aris', 'halverson', 'vera']
  const NEWS_ICONS = [
    { value: 'none', label: 'No icon' },
    { value: 'league', label: 'League mark' },
    { value: 'team', label: 'Team crest' },
  ]
  const HEADLINE_MAX = 160
  const BODY_MAX = 1200

  const [newsHeadline, setNewsHeadline] = useState('')
  const [newsBody, setNewsBody] = useState('')
  const [newsPostAs, setNewsPostAs] = useState('league')
  const [newsIcon, setNewsIcon] = useState('none')
  const [newsTeamId, setNewsTeamId] = useState('')
  const [newsPinned, setNewsPinned] = useState(false)
  // Set while editing an existing row; null while composing a new one. The same form
  // does both, so the copy cannot drift between a create form and an edit form.
  const [newsEditId, setNewsEditId] = useState<number | null>(null)
  const [newsPosted, setNewsPosted] = useState<string | null>(null)
  const [newsError, setNewsError] = useState<string | null>(null)
  const [newsLoading, setNewsLoading] = useState(false)
  const [announcements, setAnnouncements] = useState<Announcement[]>([])

  // ── Cores conversations ───────────────────────────────────────────────────
  // A written notice and a conversation are different enough to be different composers:
  // one has a headline plus optional prose, the other is N lines each belonging to a
  // named Core. Sharing one form would mean a headline field that means nothing half the
  // time.
  const CONVO_MAX_TURNS = 6
  const [newsMode, setNewsMode] = useState<'announcement' | 'conversation'>('announcement')
  const [convoTurns, setConvoTurns] = useState<{ core: string; text: string }[]>([
    { core: 'vera', text: '' }, { core: 'pyre', text: '' },
  ])
  // ⚠️ Defaults ON. A Cores row can never lead the feed on weight alone — the front page
  // keeps that category out of the lead deliberately, since the Cores are voice rather
  // than report — and pinning is the one thing that outranks the rule. So "make this the
  // headline" IS this checkbox.
  const [convoPinned, setConvoPinned] = useState(true)

  const setTurn = (index: number, patch: Partial<{ core: string; text: string }>) =>
    setConvoTurns(turns => turns.map((t, i) => (i === index ? { ...t, ...patch } : t)))
  const addTurn = () => setConvoTurns(turns => turns.length >= CONVO_MAX_TURNS ? turns
    : [...turns, { core: NEWS_CORES[turns.length % NEWS_CORES.length], text: '' }])
  const removeTurn = (index: number) =>
    setConvoTurns(turns => turns.length <= 1 ? turns : turns.filter((_, i) => i !== index))

  const convoReady = convoTurns.some(t => t.text.trim()) &&
    convoTurns.every(t => t.text.trim().length <= HEADLINE_MAX)

  const handlePostConversation = async () => {
    // Empty trailing turns are dropped rather than rejected — adding a row and not using
    // it is a normal way to compose, not a mistake worth an error message.
    const turns = convoTurns
      .map(t => ({ core: t.core, text: t.text.trim() }))
      .filter(t => t.text)
    if (!turns.length) return
    setNewsLoading(true); setNewsError(null); setNewsPosted(null)
    try {
      const res = await fetch(`${API_BASE}/admin/league-news/conversation`, {
        method: 'POST', headers: await buildHeaders(),
        body: JSON.stringify({ turns, pinned: convoPinned }),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.detail || 'Request failed')
      setNewsPosted(convoPinned
        ? 'Posted. The conversation is the headline.'
        : 'Posted to the league news.')
      setConvoTurns([{ core: 'vera', text: '' }, { core: 'pyre', text: '' }])
      setConvoPinned(true)
      loadAnnouncements()
    } catch (e: any) {
      setNewsError(e.message)
    } finally {
      setNewsLoading(false)
    }
  }

  const loadAnnouncements = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/league-news`, { headers: await buildHeaders() })
      const json = await res.json()
      if (res.ok) setAnnouncements(json.data?.items ?? [])
    } catch { /* the composer still works without the list */ }
  }

  const resetNewsForm = () => {
    setNewsEditId(null); setNewsHeadline(''); setNewsBody('')
    setNewsPostAs('league'); setNewsIcon('none'); setNewsTeamId(''); setNewsPinned(false)
  }

  const handlePostNews = async () => {
    const headline = newsHeadline.trim()
    if (!headline) return
    setNewsLoading(true); setNewsError(null); setNewsPosted(null)
    try {
      const body = {
        headline, body: newsBody.trim() || null,
        postAs: newsPostAs, icon: newsIcon,
        teamId: newsTeamId ? Number(newsTeamId) : null, pinned: newsPinned,
      }
      const res = await fetch(
        newsEditId ? `${API_BASE}/admin/league-news/${newsEditId}` : `${API_BASE}/admin/league-news`,
        { method: newsEditId ? 'PATCH' : 'POST', headers: await buildHeaders(), body: JSON.stringify(body) },
      )
      const json = await res.json()
      if (!res.ok) throw new Error(json.detail || 'Request failed')
      setNewsPosted(newsEditId ? 'Announcement updated.' : 'Posted to the league news.')
      resetNewsForm()
      loadAnnouncements()
    } catch (e: any) {
      setNewsError(e.message)
    } finally {
      setNewsLoading(false)
    }
  }

  const editAnnouncement = (a: Announcement) => {
    setNewsEditId(a.id)
    setNewsHeadline(a.headline)
    setNewsBody(a.body ?? '')
    const asCore = a.core && a.core !== 'league' ? a.core : null
    setNewsPostAs(asCore ?? 'league')
    setNewsIcon(a.core === 'league' ? 'league' : a.teamId ? 'team' : 'none')
    setNewsTeamId(a.teamId ? String(a.teamId) : '')
    setNewsPinned(a.pinned)
    setNewsPosted(null); setNewsError(null)
  }

  const setAnnouncementPinned = async (id: number, pinned: boolean) => {
    try {
      await fetch(`${API_BASE}/admin/league-news/${id}`, {
        method: 'PATCH', headers: await buildHeaders(), body: JSON.stringify({ pinned }),
      })
      loadAnnouncements()
    } catch { /* list refresh will show the truth */ }
  }

  const deleteAnnouncement = async (id: number) => {
    try {
      await fetch(`${API_BASE}/admin/league-news/${id}`, {
        method: 'DELETE', headers: await buildHeaders(),
      })
      if (newsEditId === id) resetNewsForm()
      loadAnnouncements()
    } catch { /* list refresh will show the truth */ }
  }

  const loadPool = React.useCallback(async () => {
    setPoolError(null)
    try {
      const res = await fetch(`${API_BASE}/admin/names/pool`, { headers: await buildHeaders() })
      if (!res.ok) throw new Error((await res.json()).detail || 'Request failed')
      const data = await res.json()
      setPoolNames(data.names || [])
      setPoolHeld(data.held || [])
    } catch (e: any) {
      setPoolError(e.message)
    }
  }, [buildHeaders])

  const addNames = async (names: string[]) => {
    const res = await fetch(`${API_BASE}/admin/names`, {
      method: 'POST', headers: await buildHeaders(), body: JSON.stringify({ names }),
    })
    if (!res.ok) throw new Error((await res.json()).detail || 'Request failed')
    setNamesResult(await res.json())
    setNamesInput('')
    setNameChecks(null)
    setNameChoices({})
    loadPool()
  }

  /** Check the typed names first. A batch where nothing is close goes straight in;
   *  otherwise the matches are shown and only the names left ticked are added. */
  const handleAddNames = async () => {
    const names = namesInput.split('\n').map(n => n.trim()).filter(Boolean)
    if (!names.length) return
    setNamesLoading(true)
    setNamesError(null)
    setNamesResult(null)
    try {
      const res = await fetch(`${API_BASE}/admin/names/check`, {
        method: 'POST', headers: await buildHeaders(), body: JSON.stringify({ names }),
      })
      if (!res.ok) throw new Error((await res.json()).detail || 'Request failed')
      const checks: NameCheck[] = (await res.json()).results || []
      if (checks.every(c => c.status === 'clear')) {
        await addNames(checks.map(c => c.name))
      } else {
        setNameChecks(checks)
        setNameChoices(Object.fromEntries(checks.map(c => [c.name, c.status === 'clear'])))
      }
    } catch (e: any) {
      setNamesError(e.message)
    } finally {
      setNamesLoading(false)
    }
  }

  const handleAddChosen = async () => {
    const names = (nameChecks || []).filter(c => c.status !== 'blocked' && nameChoices[c.name])
      .map(c => c.name)
    if (!names.length) return
    setNamesLoading(true)
    setNamesError(null)
    try {
      await addNames(names)
    } catch (e: any) {
      setNamesError(e.message)
    } finally {
      setNamesLoading(false)
    }
  }

  // Discord name submissions awaiting review
  type NameSubmission = {
    id: number; name: string; status: string
    submittedBy: string | null; createdAt: string | null
    similar?: NameMatch[]
  }
  const [subs, setSubs] = useState<NameSubmission[]>([])
  const [subCounts, setSubCounts] = useState<Record<string, number>>({})
  const [subFilter, setSubFilter] = useState<'pending' | 'approved' | 'rejected'>('pending')
  const [subsError, setSubsError] = useState<string | null>(null)
  const [subsBusy, setSubsBusy] = useState(false)

  const fetchSubmissions = React.useCallback(async (status = subFilter) => {
    setSubsError(null)
    try {
      const res = await fetch(`${API_BASE}/admin/names/submissions?status=${status}`, {
        headers: await buildHeaders(),
      })
      if (!res.ok) throw new Error((await res.json()).detail || 'Request failed')
      const data = await res.json()
      setSubs(data.submissions || [])
      setSubCounts(data.counts || {})
    } catch (e: any) {
      setSubsError(e.message)
    }
    // buildHeaders is stable for a given auth state; subFilter is passed explicitly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subFilter])

  const reviewSubmissions = async (ids: number[], action: 'approve' | 'reject') => {
    if (!ids.length) return
    setSubsBusy(true)
    setSubsError(null)
    try {
      const res = await fetch(`${API_BASE}/admin/names/submissions/review`, {
        method: 'POST', headers: await buildHeaders(), body: JSON.stringify({ ids, action }),
      })
      if (!res.ok) throw new Error((await res.json()).detail || 'Request failed')
      const data = await res.json()
      // A name taken while it sat in the queue stays pending — surface that rather than
      // letting the row silently disappear.
      if (data.unavailable?.length) {
        setSubsError(`No longer available (left pending): ${data.unavailable.join(', ')}`)
      }
      await fetchSubmissions(subFilter)
      if (action === 'approve') loadPool()
    } catch (e: any) {
      setSubsError(e.message)
    } finally {
      setSubsBusy(false)
    }
  }

  // Player creation
  const [position, setPosition] = useState('QB')
  const [tier, setTier] = useState('Random')
  const [count, setCount] = useState(1)
  const [createdPlayers, setCreatedPlayers] = useState<CreatedPlayer[]>([])
  const [createError, setCreateError] = useState<string | null>(null)
  const [createLoading, setCreateLoading] = useState(false)

  // Registered users (shared by card + floobits sections)
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([])

  // Card grant
  const [cardOptions, setCardOptions] = useState<CardOptions | null>(null)
  const [cardUser, setCardUser] = useState<AdminUser | null>(null)
  const [cardPlayerSearch, setCardPlayerSearch] = useState('')
  const [cardPlayerResults, setCardPlayerResults] = useState<PlayerSearchResult[]>([])
  const [cardSelectedPlayer, setCardSelectedPlayer] = useState<PlayerSearchResult | null>(null)
  const [cardEdition, setCardEdition] = useState('base')
  const [cardCategory, setCardCategory] = useState('')
  const [cardEffect, setCardEffect] = useState('')
  const [cardClassification, setCardClassification] = useState('')
  const [cardResult, setCardResult] = useState<string | null>(null)
  const [cardError, setCardError] = useState<string | null>(null)
  const [cardLoading, setCardLoading] = useState(false)
  const [playerSearching, setPlayerSearching] = useState(false)

  // User sorting/filtering
  const [userSort, setUserSort] = useState<string>('newest')
  const [userFilter, setUserFilter] = useState<string>('all')
  const [reminderLoading, setReminderLoading] = useState(false)
  const [reminderResult, setReminderResult] = useState<string | null>(null)

  const fetchUsers = useCallback(async (sort?: string, filter?: string) => {
    try {
      const params = new URLSearchParams()
      if (sort && sort !== 'newest') params.set('sort', sort === 'last_login' ? 'last_login' : sort === 'username' ? 'username' : sort === 'oldest' ? 'oldest' : '')
      if (filter && filter !== 'all') params.set('filter', filter)
      const qs = params.toString()
      const res = await fetch(`${API_BASE}/admin/users${qs ? `?${qs}` : ''}`, {
        headers: await buildHeaders(),
      })
      if (res.ok) {
        const data = await res.json()
        setAdminUsers(data.data?.users ?? [])
      }
    } catch { /* silent */ }
  }, [password])

  // Username re-roll
  const [rerollingUserId, setRerollingUserId] = useState<number | null>(null)
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null)
  const [togglingAdminId, setTogglingAdminId] = useState<number | null>(null)

  const handleToggleAdmin = async (userId: number) => {
    const target = adminUsers.find(u => u.id === userId)
    const name = target?.username || target?.email || `User #${userId}`
    const action = target?.isAdmin ? 'revoke admin access from' : 'grant admin access to'
    if (!window.confirm(`Are you sure you want to ${action} "${name}"?`)) return
    setTogglingAdminId(userId)
    try {
      const res = await fetch(`${API_BASE}/admin/users/${userId}/toggle-admin`, {
        method: 'POST', headers: await buildHeaders(),
      })
      if (!res.ok) throw new Error((await res.json()).detail || 'Request failed')
      const data = await res.json()
      const newIsAdmin = data.data?.isAdmin ?? false
      setAdminUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, isAdmin: newIsAdmin } : u
      ))
    } catch (e: any) {
      alert(`Failed to toggle admin: ${e.message}`)
    } finally {
      setTogglingAdminId(null)
    }
  }

  const handleDeleteUser = async (user: AdminUser) => {
    if (!window.confirm(`Permanently delete user "${user.username || user.email}"?\n\nThis removes ALL data: cards, rosters, picks, transactions, etc. This cannot be undone.`)) return
    setDeletingUserId(user.id)
    try {
      const res = await fetch(`${API_BASE}/admin/users/${user.id}`, {
        method: 'DELETE', headers: await buildHeaders(),
      })
      if (!res.ok) throw new Error((await res.json()).detail || 'Request failed')
      setAdminUsers(prev => prev.filter(u => u.id !== user.id))
    } catch (e: any) {
      alert(`Failed to delete user: ${e.message}`)
    } finally {
      setDeletingUserId(null)
    }
  }

  const handleRerollUsername = async (userId: number) => {
    setRerollingUserId(userId)
    try {
      const res = await fetch(`${API_BASE}/admin/users/${userId}/reroll-username`, {
        method: 'POST', headers: await buildHeaders(),
      })
      if (!res.ok) throw new Error((await res.json()).detail || 'Request failed')
      const data = await res.json()
      const newName = data.data?.newUsername
      if (newName) {
        setAdminUsers(prev => prev.map(u =>
          u.id === userId ? { ...u, username: newName } : u
        ))
      }
    } catch { /* silent */ }
    finally { setRerollingUserId(null) }
  }

  const handleSendReminders = async () => {
    if (!window.confirm('Send onboarding reminder emails to all users who haven\'t completed setup?')) return
    setReminderLoading(true)
    setReminderResult(null)
    try {
      const res = await fetch(`${API_BASE}/admin/users/send-onboarding-reminders`, {
        method: 'POST', headers: await buildHeaders(),
      })
      if (!res.ok) throw new Error((await res.json()).detail || 'Request failed')
      const data = await res.json()
      const d = data.data
      setReminderResult(`Sent ${d.sent} reminder${d.sent !== 1 ? 's' : ''}${d.failed ? ` (${d.failed} failed)` : ''} out of ${d.totalPending} pending users`)
    } catch (e: any) {
      setReminderResult(`Error: ${e.message}`)
    } finally {
      setReminderLoading(false)
    }
  }

  // Floobits grant
  const [floobitsUser, setFloobitsUser] = useState<AdminUser | null>(null)
  const [floobitsAmount, setFloobitsAmount] = useState(100)
  const [floobitsResult, setFloobitsResult] = useState<string | null>(null)
  const [floobitsError, setFloobitsError] = useState<string | null>(null)
  const [floobitsLoading, setFloobitsLoading] = useState(false)

  // Fetch card options + users on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const res = await fetch(`${API_BASE}/admin/card-options`, {
          headers: await buildHeaders(),
        })
        if (res.ok) {
          const data = await res.json()
          setCardOptions(data.data)
        }
      } catch { /* silent */ }
    }
    fetchOptions()
    fetchUsers()
  }, [password, fetchUsers])

  // Player search with debounce
  useEffect(() => {
    if (cardPlayerSearch.length < 2) { setCardPlayerResults([]); return }
    const timer = setTimeout(async () => {
      setPlayerSearching(true)
      try {
        const res = await fetch(
          `${API_BASE}/admin/players/search?q=${encodeURIComponent(cardPlayerSearch)}`,
          { headers: await buildHeaders() },
        )
        if (res.ok) {
          const data = await res.json()
          setCardPlayerResults(data.data?.players ?? [])
        }
      } catch { /* silent */ }
      finally { setPlayerSearching(false) }
    }, 300)
    return () => clearTimeout(timer)
  }, [cardPlayerSearch, password])

  const handleGrantCard = async () => {
    if (!cardUser) return
    setCardLoading(true)
    setCardError(null)
    setCardResult(null)
    try {
      const body: Record<string, any> = {
        email: cardUser.email,
        edition: cardEdition,
      }
      if (cardSelectedPlayer) body.playerId = cardSelectedPlayer.id
      if (cardCategory) body.category = cardCategory
      if (cardEffect) body.effectName = cardEffect
      if (cardClassification) body.classification = cardClassification
      const res = await fetch(`${API_BASE}/admin/grant-card`, {
        method: 'POST', headers: await buildHeaders(), body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error((await res.json()).detail || 'Request failed')
      const data = await res.json()
      const d = data.data
      const label = cardUser.username || cardUser.email
      setCardResult(`Granted ${d.edition} ${d.displayName} (${d.playerName}) to ${label}`)
    } catch (e: any) {
      setCardError(e.message)
    } finally {
      setCardLoading(false)
    }
  }

  const handleGrantFloobits = async () => {
    if (!floobitsUser || floobitsAmount <= 0) return
    setFloobitsLoading(true)
    setFloobitsError(null)
    setFloobitsResult(null)
    try {
      const res = await fetch(`${API_BASE}/admin/grant-floobits`, {
        method: 'POST', headers: await buildHeaders(),
        body: JSON.stringify({ email: floobitsUser.email, amount: floobitsAmount }),
      })
      if (!res.ok) throw new Error((await res.json()).detail || 'Request failed')
      const data = await res.json()
      const label = floobitsUser.username || floobitsUser.email
      setFloobitsResult(`Granted ${floobitsAmount} Floobits to ${label} (Balance: ${data.data.newBalance})`)
      // Update local user list balance
      setAdminUsers(prev => prev.map(u =>
        u.id === floobitsUser.id ? { ...u, floobits: data.data.newBalance } : u
      ))
    } catch (e: any) {
      setFloobitsError(e.message)
    } finally {
      setFloobitsLoading(false)
    }
  }

  // Get available effects based on selected category (or player's default), filtered by edition
  const activeCategory = cardCategory || (cardSelectedPlayer ? CATEGORY_FOR_POSITION[cardSelectedPlayer.position] : '')
  const allEffects: EffectOption[] = (() => {
    if (!cardOptions) return []
    let effects: EffectOption[]
    // If a category is selected, show that category's effects
    if (activeCategory) effects = cardOptions.effects[activeCategory] ?? []
    // Otherwise show all effects across all categories
    else effects = Object.values(cardOptions.effects).flat()
    // Filter by selected edition
    return effects.filter(ef => ef.edition === cardEdition)
  })()

  const handleCreatePlayers = async () => {
    setCreateLoading(true)
    setCreateError(null)
    try {
      const res = await fetch(`${API_BASE}/admin/players`, {
        method: 'POST', headers: await buildHeaders(), body: JSON.stringify({ position, tier, count }),
      })
      if (!res.ok) throw new Error((await res.json()).detail || 'Request failed')
      const data = await res.json()
      setCreatedPlayers(prev => [...data.created, ...prev])
    } catch (e: any) {
      setCreateError(e.message)
    } finally {
      setCreateLoading(false)
    }
  }

  // Monitor
  interface MonitorData {
    deploySafety: { safe: boolean; reason: string }
    simulation: { isActive: boolean; phase: string; lastSaved: string | null }
    season: {
      seasonNumber: number; currentWeek: number; currentWeekText: string | null
      inPlayoffs: boolean; playoffRound: string | null; isComplete: boolean
      champion: string | null; mvp: string | null
      totalGames: number; completedGames: number
    }
    liveGames: { active: number; scheduled: number; final: number }
    timing: { mode: string | null; catchingUp: boolean }
    counts: { teams: number; activePlayers: number; freeAgents: number; retiredPlayers: number; hallOfFame: number }
    memory: { rssMb: number; totalMb?: number; scheduleGames: number; gamesWithPlays: number; totalPlaysInMemory: number; pid: number }
    disk?: {
      path: string; totalMb: number; usedMb: number; freeMb: number
      dbMb: number; backupsMb: number; backupCount: number; backupKeep: number
      nextBackupNeedsMb: number; nextBackupFits: boolean
    } | null
    websockets: Record<string, any>
    personality?: {
      total: number
      baseVibes: number
      commonVariants: number
      rareVariants: number
      unassigned: number
      quirked: number
      rareVariantList: string[]
      personalityCounts: Record<string, number>
      quirkCounts: Record<string, number>
    }
  }
  const [monitorData, setMonitorData] = useState<MonitorData | null>(null)
  const [monitorError, setMonitorError] = useState<string | null>(null)
  const [monitorLoading, setMonitorLoading] = useState(false)

  const fetchMonitor = useCallback(async () => {
    setMonitorLoading(true)
    setMonitorError(null)
    try {
      const res = await fetch(`${API_BASE}/admin/monitor`, { headers: await buildHeaders() })
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
      const json = await res.json()
      setMonitorData(json.data)
    } catch (e: any) {
      setMonitorError(e.message)
    } finally {
      setMonitorLoading(false)
    }
  }, [password])

  type Section = 'monitor' | 'analytics' | 'achievements' | 'names' | 'news' | 'players' | 'cards' | 'floobits' | 'users' | 'rules' | 'settings'
  const [activeSection, setActiveSection] = useState<Section>('monitor')

  // Load the Discord submission queue lazily — only when the Names tab is open, and
  // again whenever the status filter changes. (Declared here rather than beside the
  // other name state because it reads activeSection.)
  React.useEffect(() => {
    if (activeSection === 'names') fetchSubmissions(subFilter)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, subFilter])

  React.useEffect(() => {
    if (activeSection === 'names') loadPool()
  }, [activeSection, loadPool])

  // Same lazy pattern for the announcement list.
  React.useEffect(() => {
    if (activeSection === 'news') loadAnnouncements()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection])

  // Auto-refresh monitor every 30s when active
  useEffect(() => {
    if (activeSection !== 'monitor') return
    fetchMonitor()
    const interval = setInterval(fetchMonitor, 30000)
    return () => clearInterval(interval)
  }, [activeSection, fetchMonitor])

  // ── Analytics ──────────────────────────────────────────────────────
  type AnalyticsTab = 'economy' | 'cards' | 'fantasy' | 'users' | 'funding' | 'pickem'
  const [analyticsTab, setAnalyticsTab] = useState<AnalyticsTab>('economy')
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null)
  const [analyticsError, setAnalyticsError] = useState<string | null>(null)
  const [analyticsLoading, setAnalyticsLoading] = useState(false)

  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true)
    setAnalyticsError(null)
    try {
      const res = await fetch(`${API_BASE}/admin/analytics`, { headers: await buildHeaders() })
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
      const json = await res.json()
      setAnalyticsData(json.data)
    } catch (e: any) {
      setAnalyticsError(e.message)
    } finally {
      setAnalyticsLoading(false)
    }
  }, [password])

  useEffect(() => {
    if (activeSection === 'analytics') fetchAnalytics()
  }, [activeSection, fetchAnalytics])

  // Achievements metrics
  const [achMetrics, setAchMetrics] = useState<any>(null)
  const [achError, setAchError] = useState<string | null>(null)
  const [achLoading, setAchLoading] = useState(false)

  const fetchAchievements = useCallback(async () => {
    setAchLoading(true)
    setAchError(null)
    try {
      const res = await fetch(`${API_BASE}/admin/achievements`, { headers: await buildHeaders() })
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
      const json = await res.json()
      setAchMetrics(json.data)
    } catch (e: any) {
      setAchError(e.message)
    } finally {
      setAchLoading(false)
    }
  }, [password])

  useEffect(() => {
    if (activeSection === 'achievements') fetchAchievements()
  }, [activeSection, fetchAchievements])

  // ── App Settings ──────────────────────────────────────────────────
  const [settingsForm, setSettingsForm] = useState<{
    feedback_url: string; feedback_visible: boolean; survey_url: string; survey_visible: boolean; survey_text: string
    halftime_show_url: string; halftime_show_pause_seconds: string
    anomalies_enabled: boolean; criticality_enabled: boolean; awakened_powers_enabled: boolean; trading_enabled: boolean; rule_votes_enabled: boolean; anomaly_intensity: string
    awakened_involve_qb: string; awakened_involve_rb: string; awakened_involve_wr: string; awakened_involve_te: string; awakened_involve_k: string; awakened_def_fire_chance: string
  }>({ feedback_url: '', feedback_visible: true, survey_url: '', survey_visible: false, survey_text: '', halftime_show_url: '', halftime_show_pause_seconds: '120', anomalies_enabled: false, criticality_enabled: false, awakened_powers_enabled: false, trading_enabled: false, rule_votes_enabled: true, anomaly_intensity: 'normal', awakened_involve_qb: '16', awakened_involve_rb: '13', awakened_involve_wr: '5.5', awakened_involve_te: '5.5', awakened_involve_k: '0.5', awakened_def_fire_chance: '35' })
  const [settingsLoading, setSettingsLoading] = useState(false)
  const [settingsSaved, setSettingsSaved] = useState<string | null>(null)
  const [settingsError, setSettingsError] = useState<string | null>(null)

  const fetchSettings = useCallback(async () => {
    setSettingsLoading(true)
    setSettingsError(null)
    try {
      const res = await fetch(`${API_BASE}/app-settings`)
      if (!res.ok) throw new Error(`status ${res.status}`)
      const data = await res.json()
      setSettingsForm({
        feedback_url: data.feedback_url || '',
        feedback_visible: data.feedback_visible !== false,
        survey_url: data.survey_url || '',
        survey_visible: data.survey_visible === true,
        survey_text: data.survey_text || '',
        halftime_show_url: data.halftime_show_url || '',
        halftime_show_pause_seconds: data.halftime_show_pause_seconds || '120',
        anomalies_enabled: data.anomalies_enabled === true || data.anomalies_enabled === 'true',
        criticality_enabled: data.criticality_enabled === true || data.criticality_enabled === 'true',
        trading_enabled: data.trading_enabled === true || data.trading_enabled === 'true',
        rule_votes_enabled: data.rule_votes_enabled === true || data.rule_votes_enabled === 'true',
        awakened_powers_enabled: data.awakened_powers_enabled === true || data.awakened_powers_enabled === 'true',
        anomaly_intensity: data.anomaly_intensity || 'normal',
        awakened_involve_qb: data.awakened_involve_qb != null ? String(data.awakened_involve_qb) : '16',
        awakened_involve_rb: data.awakened_involve_rb != null ? String(data.awakened_involve_rb) : '13',
        awakened_involve_wr: data.awakened_involve_wr != null ? String(data.awakened_involve_wr) : '5.5',
        awakened_involve_te: data.awakened_involve_te != null ? String(data.awakened_involve_te) : '5.5',
        awakened_involve_k: data.awakened_involve_k != null ? String(data.awakened_involve_k) : '0.5',
        awakened_def_fire_chance: data.awakened_def_fire_chance != null ? String(data.awakened_def_fire_chance) : '35',
      })
    } catch (e: any) {
      setSettingsError(e?.message || 'Failed to load settings')
    } finally {
      setSettingsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (activeSection === 'settings') fetchSettings()
  }, [activeSection, fetchSettings])

  const saveSettings = async () => {
    setSettingsLoading(true)
    setSettingsSaved(null)
    setSettingsError(null)
    try {
      const res = await fetch(`${API_BASE}/admin/app-settings`, {
        method: 'PUT',
        headers: await buildHeaders(),
        body: JSON.stringify(settingsForm),
      })
      if (!res.ok) {
        const detail = await res.text()
        throw new Error(detail || `status ${res.status}`)
      }
      // Bust the module-level cache so next page load gets fresh values
      const { invalidateAppSettings } = await import('@/hooks/useAppSettings')
      invalidateAppSettings()
      setSettingsSaved('Saved (refresh page to see across the app)')
      setTimeout(() => setSettingsSaved(null), 4000)
    } catch (e: any) {
      setSettingsError(e?.message || 'Save failed')
    } finally {
      setSettingsLoading(false)
    }
  }

  // ── Personality YAML reload ──────────────────────────────────────
  const [reloadLoading, setReloadLoading] = useState(false)
  const [reloadResult, setReloadResult] = useState<string | null>(null)
  const [reloadError, setReloadError] = useState<string | null>(null)

  const reloadPersonalityTemplates = async () => {
    setReloadLoading(true)
    setReloadResult(null)
    setReloadError(null)
    try {
      const res = await fetch(`${API_BASE}/admin/personality/reload`, {
        method: 'POST',
        headers: await buildHeaders(),
      })
      if (!res.ok) {
        const detail = await res.text()
        throw new Error(detail || `status ${res.status}`)
      }
      const data = await res.json()
      setReloadResult(
        `Loaded — ${data.personalities} personalities, ${data.quirks} quirks, ` +
        `${data.hometowns} hometowns, ${data.favoriteCategories} favorite categories`
      )
      setTimeout(() => setReloadResult(null), 6000)
    } catch (e: any) {
      setReloadError(e?.message || 'Reload failed')
    } finally {
      setReloadLoading(false)
    }
  }

  const tabs: { id: Section; label: string }[] = [
    { id: 'monitor', label: 'Monitor' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'achievements', label: 'Achievements' },
    { id: 'names', label: 'Names' },
    { id: 'news', label: 'News' },
    { id: 'players', label: 'Players' },
    { id: 'cards', label: 'Cards' },
    { id: 'floobits', label: 'Floobits' },
    { id: 'users', label: 'Users' },
    { id: 'rules', label: 'Rules' },
    { id: 'settings', label: 'Settings' },
  ]

  return (
    <div style={{ backgroundColor: '#0f172a', minHeight: '100vh', color: '#e2e8f0' }}>
    <div style={{
      borderBottom: '1px solid #1e293b', padding: '12px 24px',
      display: 'flex', justifyContent: 'center', gap: '6px', flexWrap: 'wrap',
    }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => setActiveSection(t.id)} style={{
          background: activeSection === t.id ? '#334155' : 'none',
          border: '1px solid',
          borderColor: activeSection === t.id ? '#475569' : '#334155',
          borderRadius: '4px',
          color: activeSection === t.id ? '#e2e8f0' : '#94a3b8',
          fontSize: '12px', padding: '5px 12px', cursor: 'pointer',
          fontWeight: '600', letterSpacing: '0.03em',
        }}>
          {t.label}

        </button>
      ))}
    </div>
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '32px 24px' }}>

      {/* Server Monitor */}
      {activeSection === 'monitor' && <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '700', margin: 0 }}>Server Monitor</h2>
          <button onClick={fetchMonitor} disabled={monitorLoading}
            style={{ ...btnStyle, fontSize: '12px', padding: '5px 14px', opacity: monitorLoading ? 0.5 : 1 }}>
            {monitorLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        {monitorError && (
          <div style={{ fontSize: '13px', color: '#ef4444', marginBottom: '12px' }}>{monitorError}</div>
        )}
        {monitorData && (() => {
          const { deploySafety, simulation, season, liveGames, timing, counts, memory, disk, websockets, personality } = monitorData
          const statBox: React.CSSProperties = {
            backgroundColor: '#0f172a', borderRadius: '6px', padding: '12px 14px',
          }
          const statLabel: React.CSSProperties = {
            fontSize: '10px', fontWeight: '700', color: '#64748b',
            letterSpacing: '0.02em', textTransform: 'uppercase', marginBottom: '4px',
          }
          const statValue: React.CSSProperties = {
            fontSize: '20px', fontWeight: '700', color: '#e2e8f0',
          }
          const smallStat: React.CSSProperties = {
            fontSize: '13px', color: '#cbd5e1', lineHeight: '1.8',
          }
          const totalMemMb = memory.totalMb || 2048
          const memoryPct = memory.rssMb > 0 ? Math.round((memory.rssMb / totalMemMb) * 100) : 0
          const memoryColor = memoryPct > 80 ? '#ef4444' : memoryPct > 60 ? '#f59e0b' : '#22c55e'
          // The backup needs a full DB copy of free space, so "will the next one fit" is
          // the number that matters, not the percentage: the volume filled on
          // 2026-10-20 with a backup in progress and took sign-in down.
          const diskPct = disk && disk.totalMb > 0 ? Math.round((disk.usedMb / disk.totalMb) * 100) : 0
          const diskColor = !disk ? '#94a3b8'
            : !disk.nextBackupFits || disk.freeMb < disk.totalMb * 0.1 ? '#ef4444'
            : disk.freeMb < disk.nextBackupNeedsMb * 2 ? '#f59e0b'
            : '#22c55e'

          return <>
            {/* Deploy Safety Banner */}
            <div style={{
              backgroundColor: deploySafety.safe ? '#052e16' : '#450a0a',
              border: `1px solid ${deploySafety.safe ? '#166534' : '#991b1b'}`,
              borderRadius: '6px', padding: '10px 14px', marginBottom: '16px',
              display: 'flex', alignItems: 'center', gap: '10px',
            }}>
              <span style={{
                width: '8px', height: '8px', borderRadius: '50%',
                backgroundColor: deploySafety.safe ? '#22c55e' : '#ef4444',
                flexShrink: 0,
              }} />
              <span style={{ fontSize: '13px', color: deploySafety.safe ? '#86efac' : '#fca5a5', fontWeight: '600' }}>
                {deploySafety.reason}
              </span>
            </div>

            {/* Memory + Season Overview */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '10px', marginBottom: '16px' }}>
              <div style={statBox}>
                <div style={statLabel}>Memory (RSS)</div>
                <div style={{ ...statValue, color: memoryColor }}>{memory.rssMb} MB</div>
                <div style={{ marginTop: '6px', height: '4px', backgroundColor: '#1e293b', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: '2px',
                    backgroundColor: memoryColor,
                    width: `${Math.min(100, memoryPct)}%`,
                    transition: 'width 0.3s ease',
                  }} />
                </div>
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>{memoryPct}% of {totalMemMb} MB</div>
              </div>
              <div style={statBox}>
                <div style={statLabel}>DB Volume</div>
                {disk ? (
                  <>
                    <div style={{ ...statValue, color: diskColor }}>{Math.round(disk.freeMb)} MB free</div>
                    <div style={{ marginTop: '6px', height: '4px', backgroundColor: '#1e293b', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: '2px',
                        backgroundColor: diskColor,
                        width: `${Math.min(100, diskPct)}%`,
                        transition: 'width 0.3s ease',
                      }} />
                    </div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                      {diskPct}% of {Math.round(disk.totalMb)} MB used
                    </div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                      DB {Math.round(disk.dbMb)} MB, backups {Math.round(disk.backupsMb)} MB ({disk.backupCount}/{disk.backupKeep})
                    </div>
                    <div style={{ fontSize: '11px', marginTop: '2px', color: disk.nextBackupFits ? '#94a3b8' : '#fca5a5', fontWeight: disk.nextBackupFits ? 400 : 600 }}>
                      {disk.nextBackupFits
                        ? `Next backup fits (needs ${Math.round(disk.nextBackupNeedsMb)} MB)`
                        : `Next backup won't fit (needs ${Math.round(disk.nextBackupNeedsMb)} MB)`}
                    </div>
                  </>
                ) : (
                  <div style={{ ...statValue, color: '#94a3b8' }}>—</div>
                )}
              </div>
              <div style={statBox}>
                <div style={statLabel}>Season</div>
                <div style={statValue}>S{season.seasonNumber}</div>
                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
                  {season.currentWeekText || `Week ${season.currentWeek}`}
                </div>
              </div>
              <div style={statBox}>
                <div style={statLabel}>Phase</div>
                <div style={{ ...statValue, fontSize: '16px', marginTop: '4px' }}>
                  {simulation.phase.replace(/_/g, ' ').toUpperCase()}
                </div>
                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
                  {timing.mode || '—'}{timing.catchingUp ? ' (catching up)' : ''}
                </div>
              </div>
            </div>

            {/* Games + Play Data */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px', marginBottom: '16px' }}>
              <div style={statBox}>
                <div style={statLabel}>Games</div>
                <div style={statValue}>{season.completedGames}/{season.totalGames}</div>
              </div>
              <div style={statBox}>
                <div style={statLabel}>Live</div>
                <div style={{
                  ...statValue,
                  color: liveGames.active > 0 ? '#22c55e' : '#94a3b8',
                }}>{liveGames.active}</div>
                <div style={{ fontSize: '11px', color: '#64748b' }}>
                  {liveGames.scheduled} queued / {liveGames.final} done
                </div>
              </div>
              <div style={statBox}>
                <div style={statLabel}>Games w/ Plays</div>
                <div style={{
                  ...statValue,
                  color: memory.gamesWithPlays > 24 ? '#f59e0b' : '#e2e8f0',
                }}>{memory.gamesWithPlays}</div>
                <div style={{ fontSize: '11px', color: '#64748b' }}>of {memory.scheduleGames} total</div>
              </div>
              <div style={statBox}>
                <div style={statLabel}>Plays in Memory</div>
                <div style={statValue}>{memory.totalPlaysInMemory.toLocaleString()}</div>
              </div>
            </div>

            {/* Counts + WebSockets */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div style={statBox}>
                <div style={{ ...statLabel, marginBottom: '8px' }}>Entity Counts</div>
                <div style={smallStat}>
                  Teams: {counts.teams}<br />
                  Active Players: {counts.activePlayers}<br />
                  Free Agents: {counts.freeAgents}<br />
                  Retired: {counts.retiredPlayers}<br />
                  Hall of Fame: {counts.hallOfFame}
                </div>
              </div>
              <div style={statBox}>
                <div style={{ ...statLabel, marginBottom: '8px' }}>WebSockets</div>
                <div style={smallStat}>
                  <div>Connections: {websockets.total_connections ?? 0}</div>
                  <div>Unique Users: {websockets.unique_users ?? 0}</div>
                </div>
                <div style={{ fontSize: '11px', color: '#475569', marginTop: '8px' }}>
                  PID: {memory.pid} | Last saved: {simulation.lastSaved
                    ? new Date(simulation.lastSaved).toLocaleString()
                    : '—'}
                </div>
              </div>
            </div>

            {/* Champion / MVP */}
            {(season.champion || season.mvp) && (
              <div style={{ ...statBox, marginTop: '10px', display: 'flex', gap: '24px' }}>
                {season.champion && <div style={smallStat}>Champion: <span style={{ color: '#fbbf24', fontWeight: '600' }}>{season.champion}</span></div>}
                {season.mvp && <div style={smallStat}>MVP: <span style={{ color: '#fbbf24', fontWeight: '600' }}>{season.mvp}</span></div>}
              </div>
            )}

            {/* Personality Distribution */}
            {personality && personality.total > 0 && (
              <div style={{ ...statBox, marginTop: '10px' }}>
                <div style={{ ...statLabel, marginBottom: '8px' }}>Personality Distribution</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '10px' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>Base Vibes</div>
                    <div style={{ ...statValue, fontSize: '20px' }}>{personality.baseVibes}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>Common Variants</div>
                    <div style={{ ...statValue, fontSize: '20px', color: '#a78bfa' }}>{personality.commonVariants}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>Rare Variants</div>
                    <div style={{ ...statValue, fontSize: '20px', color: '#f59e0b' }}>{personality.rareVariants}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>Quirked</div>
                    <div style={{ ...statValue, fontSize: '20px' }}>{personality.quirked}</div>
                  </div>
                </div>
                {personality.unassigned > 0 && (
                  <div style={{ fontSize: '11px', color: '#f59e0b', marginBottom: '8px' }}>
                    {personality.unassigned} unassigned (will be backfilled on next boot)
                  </div>
                )}
                {personality.rareVariantList.length > 0 && (
                  <div style={{ fontSize: '11px', color: '#cbd5e1', marginBottom: '8px' }}>
                    <span style={{ color: '#94a3b8' }}>Rare variants in league:</span>{' '}
                    {personality.rareVariantList.join(', ')}
                  </div>
                )}
                <details style={{ marginTop: '8px' }}>
                  <summary style={{ cursor: 'pointer', fontSize: '11px', color: '#94a3b8' }}>
                    Personality counts
                  </summary>
                  <div style={{ fontSize: '11px', color: '#cbd5e1', marginTop: '6px', columnCount: 2, columnGap: '20px' }}>
                    {Object.entries(personality.personalityCounts).map(([name, count]) => (
                      <div key={name}>{name}: {count}</div>
                    ))}
                  </div>
                </details>
                <details style={{ marginTop: '4px' }}>
                  <summary style={{ cursor: 'pointer', fontSize: '11px', color: '#94a3b8' }}>
                    Quirk counts
                  </summary>
                  <div style={{ fontSize: '11px', color: '#cbd5e1', marginTop: '6px', columnCount: 2, columnGap: '20px' }}>
                    {Object.entries(personality.quirkCounts).map(([name, count]) => (
                      <div key={name}>{name}: {count}</div>
                    ))}
                  </div>
                </details>
              </div>
            )}
          </>
        })()}
      </div>}

      {/* Analytics Dashboard */}
      {activeSection === 'analytics' && <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '700', margin: 0 }}>Analytics Dashboard</h2>
          <button onClick={fetchAnalytics} disabled={analyticsLoading}
            style={{ ...btnStyle, fontSize: '12px', padding: '5px 14px', opacity: analyticsLoading ? 0.5 : 1 }}>
            {analyticsLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
        {analyticsError && (
          <div style={{ fontSize: '13px', color: '#ef4444', marginBottom: '12px' }}>{analyticsError}</div>
        )}
        {analyticsData && (() => {
          const statBox: React.CSSProperties = {
            backgroundColor: '#0f172a', borderRadius: '6px', padding: '12px 14px',
          }
          const statLabel: React.CSSProperties = {
            fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase',
            letterSpacing: '0.02em', marginBottom: '4px',
          }
          const statValue: React.CSSProperties = {
            fontSize: '20px', fontWeight: '700', color: '#e2e8f0',
          }
          const smallStat: React.CSSProperties = {
            fontSize: '12px', color: '#94a3b8',
          }
          const listRow: React.CSSProperties = {
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '6px 10px', fontSize: '12px', color: '#cbd5e1',
            borderBottom: '1px solid #1e293b',
          }

          const editionColors: Record<string, string> = {
            metallic: '#94a3b8', holographic: '#bae6fd', prismatic: '#f9a8d4', diamond: '#a5f3fc',
          }
          const tierColors: Record<string, string> = {
            MEGA_MARKET: '#a78bfa', LARGE_MARKET: '#3b82f6', MID_MARKET: '#2dd4bf', SMALL_MARKET: '#f97316',
          }
          const { economy, cards, fantasy, users, funding, pickEm } = analyticsData

          const analyticsTabs: { id: AnalyticsTab; label: string }[] = [
            { id: 'economy', label: 'Economy' },
            { id: 'cards', label: 'Cards' },
            { id: 'fantasy', label: 'Fantasy' },
            { id: 'users', label: 'Users' },
            { id: 'funding', label: 'Funding' },
            { id: 'pickem', label: 'Pick-Em' },
          ]

          return <>
            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '12px' }}>Season {analyticsData.seasonNumber}</div>

            {/* Sub-tabs */}
            <div style={{
              display: 'flex', gap: '4px', marginBottom: '20px', flexWrap: 'wrap',
              borderBottom: '1px solid #334155', paddingBottom: '12px',
            }}>
              {analyticsTabs.map(t => (
                <button key={t.id} onClick={() => setAnalyticsTab(t.id)} style={{
                  background: analyticsTab === t.id ? '#334155' : 'transparent',
                  border: '1px solid',
                  borderColor: analyticsTab === t.id ? '#475569' : '#1e293b',
                  borderRadius: '4px',
                  color: analyticsTab === t.id ? '#e2e8f0' : '#94a3b8',
                  fontSize: '11px', padding: '5px 12px', cursor: 'pointer',
                  fontWeight: '600',
                }}>{t.label}</button>
              ))}
            </div>

            {/* ── Economy Health ── */}
            {analyticsTab === 'economy' && <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                <div style={statBox}>
                  <div style={statLabel}>In Circulation</div>
                  <div style={statValue}>{economy.totalCirculation.toLocaleString()}</div>
                </div>
                <div style={statBox}>
                  <div style={statLabel}>All-Time Earned</div>
                  <div style={{ ...statValue, color: '#22c55e' }}>{economy.totalEarned.toLocaleString()}</div>
                </div>
                <div style={statBox}>
                  <div style={statLabel}>All-Time Spent</div>
                  <div style={{ ...statValue, color: '#ef4444' }}>{economy.totalSpent.toLocaleString()}</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                <div style={statBox}>
                  <div style={statLabel}>Season Earnings</div>
                  <div style={{ ...statValue, color: '#22c55e', fontSize: '18px' }}>{economy.seasonEarnings.toLocaleString()}</div>
                </div>
                <div style={statBox}>
                  <div style={statLabel}>Season Spending</div>
                  <div style={{ ...statValue, color: '#ef4444', fontSize: '18px' }}>{economy.seasonSpending.toLocaleString()}</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div style={statBox}>
                  <div style={{ ...statLabel, marginBottom: '8px' }}>Earnings by Source</div>
                  {Object.entries(economy.earningsBreakdown).length === 0
                    ? <div style={smallStat}>No data yet</div>
                    : Object.entries(economy.earningsBreakdown)
                      .sort(([,a],[,b]) => b - a)
                      .map(([type, amount]) => (
                        <div key={type} style={listRow}>
                          <span>{type.replace(/_/g, ' ')}</span>
                          <span style={{ color: '#22c55e', fontWeight: '600' }}>{amount.toLocaleString()}</span>
                        </div>
                      ))
                  }
                </div>
                <div style={statBox}>
                  <div style={{ ...statLabel, marginBottom: '8px' }}>Spending by Type</div>
                  {Object.entries(economy.spendingBreakdown).length === 0
                    ? <div style={smallStat}>No data yet</div>
                    : Object.entries(economy.spendingBreakdown)
                      .sort(([,a],[,b]) => b - a)
                      .map(([type, amount]) => (
                        <div key={type} style={listRow}>
                          <span>{type.replace(/_/g, ' ')}</span>
                          <span style={{ color: '#ef4444', fontWeight: '600' }}>{amount.toLocaleString()}</span>
                        </div>
                      ))
                  }
                </div>
              </div>
              {economy.avgBalance != null && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginTop: '12px' }}>
                  <div style={statBox}>
                    <div style={statLabel}>Avg Balance</div>
                    <div style={statValue}>{economy.avgBalance?.toLocaleString()}</div>
                  </div>
                  <div style={statBox}>
                    <div style={statLabel}>Median Balance</div>
                    <div style={statValue}>{economy.medianBalance?.toLocaleString()}</div>
                  </div>
                  <div style={statBox}>
                    <div style={statLabel}>Last Weekly Payout</div>
                    <div style={statValue}>avg {economy.avgWeeklyPayout ?? 0}F</div>
                    <div style={smallStat}>
                      max {economy.maxWeeklyPayout ?? 0}F, {economy.weeklyPayoutRecipients ?? 0} users
                      {economy.lastPayoutWeek ? ` (wk ${economy.lastPayoutWeek})` : ''}
                    </div>
                  </div>
                </div>
              )}
              {(economy.richestUsers?.length ?? 0) > 0 && (
                <div style={{ ...statBox, marginTop: '12px' }}>
                  <div style={{ ...statLabel, marginBottom: '8px' }}>Top Balances</div>
                  {economy.richestUsers!.map((u, i) => (
                    <div key={i} style={listRow}>
                      <span>{i + 1}. {u.username}</span>
                      <span style={{ fontWeight: '600', color: '#f59e0b' }}>{u.balance.toLocaleString()}F</span>
                    </div>
                  ))}
                </div>
              )}
            </>}

            {/* ── Card Analytics ── */}
            {analyticsTab === 'cards' && <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                <div style={statBox}>
                  <div style={statLabel}>Total Cards</div>
                  <div style={statValue}>{cards.totalCards.toLocaleString()}</div>
                </div>
                {['metallic', 'holographic', 'prismatic', 'diamond'].map(ed => (
                  <div key={ed} style={statBox}>
                    <div style={statLabel}>{ed}</div>
                    <div style={{ ...statValue, color: editionColors[ed] || '#e2e8f0', fontSize: '18px' }}>
                      {(cards.byEdition[ed] || 0).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                <div style={statBox}>
                  <div style={{ ...statLabel, marginBottom: '8px' }}>Acquisition Source</div>
                  {Object.entries(cards.bySource).length === 0
                    ? <div style={smallStat}>No data yet</div>
                    : Object.entries(cards.bySource)
                      .sort(([,a],[,b]) => b - a)
                      .map(([src, cnt]) => (
                        <div key={src} style={listRow}>
                          <span>{src.replace(/_/g, ' ')}</span>
                          <span style={{ fontWeight: '600' }}>{cnt.toLocaleString()}</span>
                        </div>
                      ))
                  }
                </div>
                <div style={statBox}>
                  <div style={{ ...statLabel, marginBottom: '8px' }}>Top Equipped Effects</div>
                  <div style={{ ...smallStat, marginBottom: '6px' }}>user-weeks equipped</div>
                  {cards.topEffects.length === 0
                    ? <div style={smallStat}>No data yet</div>
                    : cards.topEffects.map((ef, i) => (
                      <div key={i} style={listRow}>
                        <HoverTooltip text={ef.tooltip || ''}><span>{ef.effectName}</span></HoverTooltip>
                        <span style={{ fontWeight: '600' }}>{ef.count}</span>
                      </div>
                    ))
                  }
                </div>
              </div>
              {Object.keys(cards.packOpenings).length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Object.keys(cards.packOpenings).length}, 1fr)`, gap: '10px' }}>
                  {Object.entries(cards.packOpenings).map(([name, cnt]) => (
                    <div key={name} style={statBox}>
                      <div style={statLabel}>{name} packs</div>
                      <div style={{ ...statValue, fontSize: '18px' }}>{cnt.toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '12px' }}>
                {(cards.bottomEffects?.length ?? 0) > 0 && (
                  <div style={statBox}>
                    <div style={{ ...statLabel, marginBottom: '8px' }}>Least Equipped Effects</div>
                    <div style={{ ...smallStat, marginBottom: '6px' }}>user-weeks equipped</div>
                    {cards.bottomEffects!.map((ef, i) => (
                      <div key={i} style={listRow}>
                        <HoverTooltip text={ef.tooltip || ''}><span>{ef.effectName}</span></HoverTooltip>
                        <span style={{ fontWeight: '600', color: '#94a3b8' }}>{ef.count}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div style={statBox}>
                  <div style={{ ...statLabel, marginBottom: '8px' }}>Combine Usage</div>
                  {!cards.combineUsage || Object.keys(cards.combineUsage).length === 0
                    ? <div style={smallStat}>No upgrades yet</div>
                    : Object.entries(cards.combineUsage).map(([type, cnt]) => (
                      <div key={type} style={listRow}>
                        <span>{type.replace(/_/g, ' ')}</span>
                        <span style={{ fontWeight: '600' }}>{cnt}</span>
                      </div>
                    ))
                  }
                  {(cards.totalCombineUses ?? 0) > 0 && (
                    <div style={{ ...smallStat, marginTop: '6px' }}>{cards.totalCombineUses} total operations</div>
                  )}
                </div>
              </div>
              {cards.usersWhoEquipped != null && (
                <div style={{ ...statBox, marginTop: '12px' }}>
                  <div style={statLabel}>Users Equipped This Season</div>
                  <div style={{ ...statValue, fontSize: '18px' }}>{cards.usersWhoEquipped}</div>
                  <div style={smallStat}>
                    {users.onboardedCount > 0 ? `${Math.round(cards.usersWhoEquipped / users.onboardedCount * 100)}%` : '0%'} of onboarded users
                  </div>
                </div>
              )}
            </>}

            {/* ── Fantasy Engagement ── */}
            {analyticsTab === 'fantasy' && <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                <div style={statBox}>
                  <div style={statLabel}>Rosters</div>
                  <div style={statValue}>{fantasy.totalRosters}</div>
                </div>
                <div style={statBox}>
                  <div style={statLabel}>Avg Points</div>
                  <div style={statValue}>{fantasy.avgTotalPoints}</div>
                </div>
                <div style={statBox}>
                  <div style={statLabel}>Avg Card Bonus</div>
                  <div style={statValue}>{fantasy.avgCardBonus}</div>
                </div>
                <div style={statBox}>
                  <div style={statLabel}>Swaps Used</div>
                  <div style={statValue}>{fantasy.totalSwapsUsed}</div>
                  <div style={smallStat}>{fantasy.totalPurchasedSwaps} purchased</div>
                </div>
              </div>
              {fantasy.topRosteredPlayers.length > 0 && (
                <div style={statBox}>
                  <div style={{ ...statLabel, marginBottom: '8px' }}>Most Rostered Players</div>
                  {fantasy.topRosteredPlayers.map((p, i) => (
                    <div key={i} style={listRow}>
                      <span>{i + 1}. {p.name}</span>
                      <span style={{ fontWeight: '600' }}>{p.count} rosters</span>
                    </div>
                  ))}
                </div>
              )}
            </>}

            {/* ── User Engagement ── */}
            {analyticsTab === 'users' && <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '10px', marginBottom: '12px' }}>
                <div style={statBox}>
                  <div style={statLabel}>Total Users</div>
                  <div style={statValue}>{users.totalUsers}</div>
                </div>
                <div style={statBox}>
                  <div style={statLabel}>Active 7d</div>
                  <div style={{ ...statValue, color: users.active7d > 0 ? '#22c55e' : '#e2e8f0' }}>{users.active7d}</div>
                </div>
                <div style={statBox}>
                  <div style={statLabel}>Active 30d</div>
                  <div style={statValue}>{users.active30d}</div>
                </div>
                <div style={statBox}>
                  <div style={statLabel}>Onboarding</div>
                  <div style={statValue}>{users.onboardingRate}%</div>
                  <div style={smallStat}>{users.onboardedCount} / {users.totalUsers}</div>
                </div>
                <div style={statBox}>
                  <div style={statLabel}>Churn Risk</div>
                  <div style={{ ...statValue, color: (users.churnRiskCount ?? 0) > 0 ? '#ef4444' : '#22c55e' }}>{users.churnRiskCount ?? 0}</div>
                  <div style={smallStat}>14d+ inactive</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                {[
                  { label: 'Fantasy', count: users.adoption.fantasy },
                  { label: 'Cards Equipped', count: cards.usersWhoEquipped ?? 0 },
                  { label: 'Pick-Em', count: users.adoption.pickEm },
                  { label: 'Funding', count: users.adoption.funding },
                ].map(({ label, count }) => (
                  <div key={label} style={statBox}>
                    <div style={statLabel}>{label}</div>
                    <div style={{ ...statValue, fontSize: '18px' }}>{count}</div>
                    <div style={smallStat}>
                      {users.onboardedCount > 0 ? `${Math.round(count / users.onboardedCount * 100)}%` : '0%'} of onboarded users
                    </div>
                  </div>
                ))}
              </div>
              {users.onboardingFunnel && (
                <div style={{ ...statBox, marginBottom: '12px' }}>
                  <div style={{ ...statLabel, marginBottom: '8px' }}>Onboarding Funnel</div>
                  {[
                    { label: 'Has Account', count: users.onboardingFunnel.hasAccount },
                    { label: 'Picked Username', count: users.onboardingFunnel.pickedUsername },
                    { label: 'Chose Favorite Team', count: users.onboardingFunnel.choseFavTeam },
                    { label: 'Drafted Fantasy Roster', count: users.onboardingFunnel.draftedRoster },
                    { label: 'Equipped Cards', count: cards.usersWhoEquipped ?? 0 },
                  ].map(({ label, count }) => (
                    <div key={label} style={listRow}>
                      <span>{label}</span>
                      <span style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{ fontWeight: '600' }}>{count}</span>
                        <span style={{ color: '#64748b', fontSize: '11px' }}>
                          {users.onboardingFunnel!.hasAccount > 0
                            ? `${Math.round(count / users.onboardingFunnel!.hasAccount * 100)}%`
                            : '0%'}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {(users.dailyActiveUsers?.length ?? 0) > 0 && (
                <div style={{ ...statBox, marginBottom: '12px' }}>
                  <div style={{ ...statLabel, marginBottom: '8px' }}>Daily Active (Last 28d)</div>
                  <div style={{ display: 'flex', gap: '2px', alignItems: 'flex-end', height: '40px' }}>
                    {(() => {
                      const dau = users.dailyActiveUsers!
                      const maxCount = Math.max(...dau.map(d => d.count))
                      return dau.map((d, i) => (
                        <div key={i} title={`${d.date}: ${d.count} users`} style={{
                          flex: 1, backgroundColor: '#3b82f6', borderRadius: '1px',
                          height: maxCount > 0 ? `${(d.count / maxCount) * 100}%` : '0',
                          minHeight: d.count > 0 ? '2px' : '0',
                        }} />
                      ))
                    })()}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                    <span style={{ fontSize: '10px', color: '#64748b' }}>
                      {users.dailyActiveUsers![0]?.date}
                    </span>
                    <span style={{ fontSize: '10px', color: '#64748b' }}>
                      {users.dailyActiveUsers![users.dailyActiveUsers!.length - 1]?.date}
                    </span>
                  </div>
                </div>
              )}
              {users.favoriteTeams.length > 0 && (
                <div style={statBox}>
                  <div style={{ ...statLabel, marginBottom: '8px' }}>Favorite Teams</div>
                  {users.favoriteTeams.map((ft, i) => (
                    <div key={i} style={listRow}>
                      <span>{ft.team}</span>
                      <span style={{ fontWeight: '600' }}>{ft.count} fans</span>
                    </div>
                  ))}
                </div>
              )}
            </>}

            {/* ── Team Funding ── */}
            {analyticsTab === 'funding' && <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                <div style={statBox}>
                  <div style={statLabel}>Fan Contributions</div>
                  <div style={statValue}>{funding.totalFanContributions.toLocaleString()}</div>
                </div>
                {['MEGA_MARKET', 'LARGE_MARKET', 'MID_MARKET', 'SMALL_MARKET'].map(tier => (
                  <div key={tier} style={statBox}>
                    <div style={statLabel}>{tier.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}</div>
                    <div style={{ ...statValue, color: tierColors[tier] || '#e2e8f0', fontSize: '18px' }}>
                      {funding.tierDistribution[tier] || 0}
                    </div>
                  </div>
                ))}
              </div>
              {funding.topTeams.length > 0 && (
                <div style={statBox}>
                  <div style={{ ...statLabel, marginBottom: '8px' }}>Top Funded Teams</div>
                  {funding.topTeams.map((t, i) => (
                    <div key={i} style={listRow}>
                      <span>{t.team}</span>
                      <span style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <span style={{ color: tierColors[t.tier] || '#94a3b8', fontSize: '11px' }}>
                          {(t.tier || '').replace(/_/g, ' ')}
                        </span>
                        <span style={{ fontWeight: '600' }}>{t.contributions.toLocaleString()}F</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>}

            {/* ── Pick-Em ── */}
            {analyticsTab === 'pickem' && <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                <div style={statBox}>
                  <div style={statLabel}>Total Picks</div>
                  <div style={statValue}>{pickEm.totalPicks.toLocaleString()}</div>
                </div>
                <div style={statBox}>
                  <div style={statLabel}>Accuracy</div>
                  <div style={statValue}>{pickEm.accuracy}%</div>
                </div>
                <div style={statBox}>
                  <div style={statLabel}>Participants</div>
                  <div style={statValue}>{pickEm.participants}</div>
                </div>
              </div>
              {(pickEm.trend?.length ?? 0) > 0 && (
                <div style={{ ...statBox, marginTop: '12px' }}>
                  <div style={{ ...statLabel, marginBottom: '8px' }}>Weekly Participation</div>
                  {pickEm.trend!.map(w => (
                    <div key={w.week} style={listRow}>
                      <span>Week {w.week}</span>
                      <span style={{ display: 'flex', gap: '16px' }}>
                        <span style={{ color: '#94a3b8', fontSize: '11px' }}>{w.participants} users</span>
                        <span style={{ fontWeight: '600' }}>{w.picks} picks</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>}
          </>
        })()}
      </div>}

      {/* Name Pool */}
      {activeSection === 'names' && <div style={sectionStyle}>
        <h2 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px' }}>Name Pool</h2>
        <div style={{ marginBottom: '12px' }}>
          <div style={labelStyle}>Add Names (one per line)</div>
          <textarea
            value={namesInput}
            onChange={e => setNamesInput(e.target.value)}
            rows={6}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
            placeholder={'John Smith\nJane Doe\nAlex Johnson'}
          />
        </div>
        <button
          onClick={handleAddNames}
          disabled={namesLoading || !namesInput.trim()}
          style={{ ...btnStyle, opacity: namesLoading || !namesInput.trim() ? 0.5 : 1 }}
        >
          {namesLoading ? 'Checking…' : 'Add Names'}
        </button>
        <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '6px' }}>
          Each name is checked first against the pool, players past and present, coaches and
          Discord suggestions. If any read close to an existing name you choose which to add.
        </div>

        {nameChecks && (
          <div style={{ marginTop: '14px', padding: '12px', background: '#1e293b', borderRadius: '6px' }}>
            <div style={{ fontSize: '13px', color: '#e2e8f0', fontWeight: 600, marginBottom: '8px' }}>
              Some of these are close to names already in the league. Tick the ones to add.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {nameChecks.map(c => (
                <label key={c.name} style={{
                  display: 'flex', gap: '10px', alignItems: 'flex-start',
                  cursor: c.status === 'blocked' ? 'default' : 'pointer',
                }}>
                  <input
                    type="checkbox"
                    disabled={c.status === 'blocked'}
                    checked={c.status !== 'blocked' && !!nameChoices[c.name]}
                    onChange={e => setNameChoices(prev => ({ ...prev, [c.name]: e.target.checked }))}
                    style={{ marginTop: '3px' }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <span style={{ fontSize: '14px', fontWeight: 600,
                      color: c.status === 'blocked' ? '#94a3b8' : '#e2e8f0' }}>{c.name}</span>
                    <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: 600,
                      color: c.status === 'clear' ? '#22c55e' : c.status === 'similar' ? '#f59e0b' : '#f87171' }}>
                      {c.status === 'clear' ? 'No close names' : c.status === 'similar' ? 'Close to' : "Can't add, already taken"}
                    </span>
                    {c.matches.length > 0 && <NameMatches matches={c.matches} />}
                  </div>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              {(() => {
                const n = nameChecks.filter(c => c.status !== 'blocked' && nameChoices[c.name]).length
                return (
                  <button onClick={handleAddChosen} disabled={namesLoading || n === 0}
                    style={{ ...btnStyle, opacity: namesLoading || n === 0 ? 0.5 : 1 }}>
                    Add {n} name{n !== 1 ? 's' : ''}
                  </button>
                )
              })()}
              <button onClick={() => { setNameChecks(null); setNameChoices({}) }}
                style={{ ...btnStyle, background: '#334155' }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {namesResult && (
          <div style={{ marginTop: '10px', fontSize: '13px', color: '#22c55e' }}>
            Added {namesResult.added} name{namesResult.added !== 1 ? 's' : ''}. Pool total: {namesResult.total}
            {(namesResult.rejectedInPool?.length || 0) > 0 && (
              <div style={{ color: '#f59e0b' }}>Already in the pool: {namesResult.rejectedInPool!.join(', ')}</div>
            )}
            {(namesResult.rejectedInUse?.length || 0) > 0 && (
              <div style={{ color: '#f59e0b' }}>Already taken: {namesResult.rejectedInUse!.join(', ')}</div>
            )}
          </div>
        )}
        {namesError && (
          <div style={{ marginTop: '10px', fontSize: '13px', color: '#ef4444' }}>{namesError}</div>
        )}

        {/* The pool itself */}
        <div style={{ borderTop: '1px solid #334155', marginTop: '24px', paddingTop: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: '16px', fontWeight: '700' }}>
              Unused Names <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: 500 }}>{poolNames.length}</span>
            </h2>
            <input
              value={poolFilter}
              onChange={e => setPoolFilter(e.target.value)}
              placeholder="Filter"
              style={{ ...inputStyle, width: '220px', padding: '6px 10px' }}
            />
          </div>
          {poolError && <div style={{ fontSize: '13px', color: '#ef4444' }}>{poolError}</div>}
          {(() => {
            const q = poolFilter.trim().toLowerCase()
            const shown = q ? poolNames.filter(n => n.toLowerCase().includes(q)) : poolNames
            return (
              <>
                {q && <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '6px' }}>{shown.length} of {poolNames.length}</div>}
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: '2px 16px', maxHeight: '320px', overflowY: 'auto',
                  padding: '8px 10px', background: '#1e293b', borderRadius: '6px',
                }}>
                  {shown.length === 0
                    ? <div style={{ fontSize: '13px', color: '#94a3b8' }}>No names{q ? ' match' : ''}.</div>
                    : shown.map(n => (
                      <div key={n} style={{ fontSize: '13px', color: '#cbd5e1', padding: '2px 0',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n}</div>
                    ))}
                </div>
              </>
            )
          })()}
          {poolHeld.length > 0 && (
            <details style={{ marginTop: '10px' }}>
              <summary style={{ fontSize: '13px', color: '#cbd5e1', cursor: 'pointer' }}>
                {poolHeld.length} held for reuse (recently retired, not drawn until their season)
              </summary>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: '2px 16px', marginTop: '6px', padding: '8px 10px', background: '#1e293b', borderRadius: '6px' }}>
                {poolHeld.map(h => (
                  <div key={h.name} style={{ fontSize: '13px', color: '#cbd5e1' }}>
                    {h.name} <span style={{ color: '#94a3b8' }}>(season {h.availableSeason})</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* Discord suggestions — these do NOT enter the pool until approved here. */}
        <div style={{ borderTop: '1px solid #334155', marginTop: '24px', paddingTop: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: '700' }}>
              Submitted from Discord
              {subCounts.pending ? (
                <span style={{ marginLeft: '8px', fontSize: '12px', fontWeight: '600',
                  color: '#0f172a', background: '#f59e0b', borderRadius: '10px', padding: '2px 8px' }}>
                  {subCounts.pending} pending
                </span>
              ) : null}
            </h2>
            <div style={{ display: 'flex', gap: '6px' }}>
              {(['pending', 'approved', 'rejected'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => setSubFilter(s)}
                  style={{
                    ...btnStyle,
                    padding: '4px 10px', fontSize: '12px', textTransform: 'capitalize',
                    background: subFilter === s ? '#334155' : 'transparent',
                    color: subFilter === s ? '#e2e8f0' : '#94a3b8',
                  }}
                >
                  {s}{subCounts[s] != null ? ` (${subCounts[s]})` : ''}
                </button>
              ))}
            </div>
          </div>
          <p style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '12px' }}>
            Names suggested with <code>/name</code> by Discord-linked users. Approving adds the
            name to the pool with the same checks as adding it above. Rejected names are kept on
            record so they can't be resubmitted.
          </p>

          {subFilter === 'pending' && subs.length > 1 && (
            <button
              onClick={() => reviewSubmissions(subs.map(s => s.id), 'approve')}
              disabled={subsBusy}
              style={{ ...btnStyle, marginBottom: '10px', opacity: subsBusy ? 0.5 : 1 }}
            >
              Approve all {subs.length}
            </button>
          )}

          {subs.length === 0 ? (
            <div style={{ fontSize: '13px', color: '#94a3b8' }}>
              No {subFilter} submissions.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {subs.map(s => (
                <div key={s.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: '12px', padding: '8px 10px', background: '#1e293b', borderRadius: '6px',
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '14px', color: '#e2e8f0', fontWeight: '600' }}>{s.name}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                      {s.submittedBy || 'unknown'}
                      {s.createdAt ? `, ${new Date(s.createdAt).toLocaleDateString()}` : ''}
                    </div>
                    {s.status === 'pending' && (s.similar?.length || 0) > 0 && (
                      <div style={{ fontSize: '11px', color: '#f59e0b', marginTop: '2px' }}>
                        Close to:
                        <NameMatches matches={s.similar!} />
                      </div>
                    )}
                  </div>
                  {s.status === 'pending' ? (
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      <button
                        onClick={() => reviewSubmissions([s.id], 'approve')}
                        disabled={subsBusy}
                        style={{ ...btnStyle, padding: '4px 12px', fontSize: '12px',
                          background: '#16a34a', opacity: subsBusy ? 0.5 : 1 }}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => reviewSubmissions([s.id], 'reject')}
                        disabled={subsBusy}
                        style={{ ...btnStyle, padding: '4px 12px', fontSize: '12px',
                          background: '#7f1d1d', opacity: subsBusy ? 0.5 : 1 }}
                      >
                        Reject
                      </button>
                    </div>
                  ) : (
                    <span style={{ fontSize: '12px', color: s.status === 'approved' ? '#22c55e' : '#f87171',
                      textTransform: 'capitalize', flexShrink: 0 }}>
                      {s.status}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          {subsError && (
            <div style={{ marginTop: '10px', fontSize: '13px', color: '#ef4444' }}>{subsError}</div>
          )}
        </div>
      </div>}

      {/* League News — hand-written rows in the feed everything else publishes to
          automatically. Their own `announcement` category, so a reader can tell a
          written notice from a reported result and the feed's per-category caps
          cannot drop it. */}
      {activeSection === 'news' && <div style={sectionStyle}>
        <h2 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '6px' }}>
          {newsEditId ? 'Edit announcement' : 'Post to League News'}
        </h2>
        <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '16px', lineHeight: 1.5 }}>
          Goes to the front page feed and out over the websocket. Season and week are
          stamped from the sim. An unpinned notice is pushed out once about forty newer
          items are published, which a busy slate does in a day. Pin it to hold it.
          Posting as a Core writes a line in their voice, threaded with the sim's own.
        </div>

        {/* A notice and a conversation are different shapes, so they get different
            composers rather than one form with fields that mean nothing half the time.
            Hidden while editing — the edit form belongs to the row being edited. */}
        {!newsEditId && (
          <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
            {([['announcement', 'Announcement'], ['conversation', 'Cores conversation']] as const)
              .map(([mode, label]) => (
                <button
                  key={mode}
                  onClick={() => { setNewsMode(mode); setNewsPosted(null); setNewsError(null) }}
                  style={{
                    ...btnStyle, padding: '6px 14px', fontSize: '12px',
                    background: newsMode === mode ? '#2563eb' : '#334155',
                  }}
                >{label}</button>
              ))}
          </div>
        )}

        {newsMode === 'conversation' && !newsEditId ? (
          <div>
            <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '14px', lineHeight: 1.5 }}>
              Several turns, published as one exchange. The feed folds it into a single
              entry with the turns in order, each in its own Core's color, exactly like a
              conversation the sim writes for itself.
            </div>

            {convoTurns.map((turn, index) => (
              <div key={index} style={{
                display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '10px',
              }}>
                <select
                  value={turn.core}
                  onChange={e => setTurn(index, { core: e.target.value })}
                  style={{ ...inputStyle, width: '130px', flexShrink: 0 }}
                >
                  {NEWS_CORES.map(c => (
                    <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                  ))}
                </select>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <input
                    value={turn.text}
                    onChange={e => setTurn(index, { text: e.target.value.slice(0, HEADLINE_MAX) })}
                    style={inputStyle}
                    placeholder={index === 0 ? 'The opening line' : 'The reply'}
                  />
                  <div style={{
                    fontSize: '11px', marginTop: '4px', textAlign: 'right',
                    color: turn.text.length > HEADLINE_MAX - 20 ? '#f59e0b' : '#64748b',
                  }}>{turn.text.length} / {HEADLINE_MAX}</div>
                </div>
                <button
                  onClick={() => removeTurn(index)}
                  disabled={convoTurns.length <= 1}
                  aria-label="Remove turn"
                  style={{
                    ...btnStyle, padding: '8px 12px', fontSize: '12px', background: '#334155',
                    opacity: convoTurns.length <= 1 ? 0.4 : 1, flexShrink: 0,
                  }}
                >Remove</button>
              </div>
            ))}

            <button
              onClick={addTurn}
              disabled={convoTurns.length >= CONVO_MAX_TURNS}
              style={{
                ...btnStyle, padding: '6px 12px', fontSize: '12px', background: '#334155',
                opacity: convoTurns.length >= CONVO_MAX_TURNS ? 0.4 : 1, marginBottom: '14px',
              }}
            >Add turn</button>

            <label style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              fontSize: '13px', color: '#cbd5e1', cursor: 'pointer', padding: '8px 0 14px',
            }}>
              <input type="checkbox" checked={convoPinned}
                     onChange={e => setConvoPinned(e.target.checked)} />
              Make it the headline
            </label>
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '-10px', marginBottom: '14px' }}>
              Cores lines never lead the feed on their own — pinning is what puts one on
              top. Leave this off and it sits in the feed as ordinary chatter.
            </div>

            <button
              onClick={handlePostConversation}
              disabled={newsLoading || !convoReady}
              style={{ ...btnStyle, opacity: newsLoading || !convoReady ? 0.5 : 1 }}
            >{newsLoading ? 'Posting…' : 'Post conversation'}</button>
            {newsPosted && (
              <div style={{ marginTop: '10px', fontSize: '13px', color: '#22c55e' }}>{newsPosted}</div>
            )}
            {newsError && (
              <div style={{ marginTop: '10px', fontSize: '13px', color: '#ef4444' }}>{newsError}</div>
            )}
          </div>
        ) : (
        <>
        <div style={{ marginBottom: '12px' }}>
          <div style={labelStyle}>Headline</div>
          <input
            value={newsHeadline}
            onChange={e => setNewsHeadline(e.target.value.slice(0, HEADLINE_MAX))}
            style={inputStyle}
            placeholder="The league will pause after tonight's slate"
          />
          <div style={{
            fontSize: '11px', marginTop: '4px', textAlign: 'right',
            color: newsHeadline.length > HEADLINE_MAX - 20 ? '#f59e0b' : '#64748b',
          }}>{newsHeadline.length} / {HEADLINE_MAX}</div>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <div style={labelStyle}>Body (optional)</div>
          <textarea
            value={newsBody}
            onChange={e => setNewsBody(e.target.value.slice(0, BODY_MAX))}
            rows={5}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
            placeholder="Anything that needs more than a headline. Line breaks are kept."
          />
          <div style={{
            fontSize: '11px', marginTop: '4px', textAlign: 'right',
            color: newsBody.length > BODY_MAX - 100 ? '#f59e0b' : '#64748b',
          }}>{newsBody.length} / {BODY_MAX}</div>
        </div>

        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '14px' }}>
          <div>
            <div style={labelStyle}>Post as</div>
            <select
              value={newsPostAs}
              onChange={e => setNewsPostAs(e.target.value)}
              style={{ ...inputStyle, maxWidth: '200px' }}
            >
              <option value="league">The league</option>
              {NEWS_CORES.map(c => (
                <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
              ))}
            </select>
          </div>
          {/* A Core post already carries that Core's icon and color, so the icon
              picker only applies to a league notice. */}
          {newsPostAs === 'league' && (
            <div>
              <div style={labelStyle}>Icon</div>
              <select
                value={newsIcon}
                onChange={e => setNewsIcon(e.target.value)}
                style={{ ...inputStyle, maxWidth: '200px' }}
              >
                {NEWS_ICONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}
          {newsPostAs === 'league' && newsIcon === 'team' && (
            <div>
              <div style={labelStyle}>Team id</div>
              <input
                value={newsTeamId}
                onChange={e => setNewsTeamId(e.target.value.replace(/[^0-9]/g, ''))}
                style={{ ...inputStyle, maxWidth: '120px' }}
                placeholder="1-32"
              />
            </div>
          )}
          <label style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            fontSize: '13px', color: '#cbd5e1', cursor: 'pointer', paddingBottom: '8px',
          }}>
            <input type="checkbox" checked={newsPinned} onChange={e => setNewsPinned(e.target.checked)} />
            Pin to the top of the feed
          </label>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            onClick={handlePostNews}
            disabled={newsLoading || !newsHeadline.trim()}
            style={{ ...btnStyle, opacity: newsLoading || !newsHeadline.trim() ? 0.5 : 1 }}
          >
            {newsLoading ? 'Saving…' : newsEditId ? 'Save changes' : 'Post announcement'}
          </button>
          {newsEditId && (
            <button onClick={resetNewsForm} style={{ ...btnStyle, background: '#334155' }}>
              Cancel
            </button>
          )}
        </div>
        {newsPosted && (
          <div style={{ marginTop: '10px', fontSize: '13px', color: '#22c55e' }}>{newsPosted}</div>
        )}
        {newsError && (
          <div style={{ marginTop: '10px', fontSize: '13px', color: '#ef4444' }}>{newsError}</div>
        )}
        </>
        )}

        <h3 style={{ fontSize: '14px', fontWeight: 700, margin: '28px 0 10px' }}>
          Posted announcements
        </h3>
        {announcements.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#64748b' }}>Nothing posted yet.</div>
        ) : announcements.map(a => (
          <div key={a.id} style={{
            display: 'flex', alignItems: 'flex-start', gap: '12px',
            padding: '10px 12px', marginBottom: '8px',
            background: a.pinned ? 'rgba(244,114,182,0.08)' : '#0f172a',
            border: `1px solid ${a.pinned ? 'rgba(244,114,182,0.35)' : '#1e293b'}`,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#e2e8f0' }}>
                {a.pinned && <span style={{ color: '#f472b6', marginRight: '7px' }}>PINNED</span>}
                {a.turns && <span style={{ color: '#38bdf8', marginRight: '7px' }}>CONVERSATION</span>}
                {a.headline}
              </div>
              {/* A conversation shows every turn, so the delete button is attached to
                  something the admin can actually read. It removes the whole exchange. */}
              {a.turns && a.turns.length > 1 && (
                <div style={{ marginTop: '6px', paddingLeft: '10px', borderLeft: '2px solid #1e293b' }}>
                  {a.turns.slice(1).map((t, i) => (
                    <div key={i} style={{ fontSize: '12px', color: '#94a3b8', marginTop: '3px' }}>
                      <span style={{ color: '#cbd5e1' }}>
                        {t.core ? t.core.charAt(0).toUpperCase() + t.core.slice(1) : '—'}:
                      </span>{' '}{t.text}
                    </div>
                  ))}
                </div>
              )}
              {a.body && (
                <div style={{
                  fontSize: '12px', color: '#94a3b8', marginTop: '4px',
                  whiteSpace: 'pre-wrap', maxHeight: '60px', overflow: 'hidden',
                }}>{a.body}</div>
              )}
              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '5px' }}>
                Season {a.season}, week {a.week}
                {a.core ? `, ${a.core}` : a.teamId ? `, team ${a.teamId}` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
              <button
                onClick={() => setAnnouncementPinned(a.id, !a.pinned)}
                style={{ ...btnStyle, padding: '5px 10px', fontSize: '12px', background: '#334155' }}
              >{a.pinned ? 'Unpin' : 'Pin'}</button>
              {/* No Edit for a conversation: the edit form is single-row, and pointing it
                  at one turn of an exchange would rewrite that line while presenting it as
                  the whole thing. Delete and repost instead. */}
              {!a.turns && (
                <button
                  onClick={() => editAnnouncement(a)}
                  style={{ ...btnStyle, padding: '5px 10px', fontSize: '12px', background: '#334155' }}
                >Edit</button>
              )}
              <button
                onClick={() => deleteAnnouncement(a.id)}
                style={{ ...btnStyle, padding: '5px 10px', fontSize: '12px', background: '#7f1d1d' }}
              >Delete</button>
            </div>
          </div>
        ))}
      </div>}


      {/* Create Players */}
      {activeSection === 'players' && <div style={sectionStyle}>
        <h2 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px' }}>Create Players</h2>
        <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
          New players are added to the free agent pool and will be available next offseason.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: '16px', marginBottom: '16px' }}>
          <div>
            <div style={labelStyle}>Position</div>
            <select value={position} onChange={e => setPosition(e.target.value)} style={selectStyle}>
              {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <div style={labelStyle}>Tier</div>
            <select value={tier} onChange={e => setTier(e.target.value)} style={selectStyle}>
              {TIERS.map(t => <option key={t} value={t}>{TIER_LABELS[t]}</option>)}
            </select>
          </div>
          <div>
            <div style={labelStyle}>Count</div>
            <input
              type="number" min={1} max={10} value={count}
              onChange={e => setCount(Math.max(1, Math.min(10, Number(e.target.value))))}
              style={inputStyle}
            />
          </div>
        </div>
        <button onClick={handleCreatePlayers} disabled={createLoading}
          style={{ ...btnStyle, opacity: createLoading ? 0.5 : 1 }}>
          {createLoading ? 'Creating…' : 'Create'}
        </button>
        {createError && (
          <div style={{ marginTop: '10px', fontSize: '13px', color: '#ef4444' }}>{createError}</div>
        )}
        {createdPlayers.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ ...labelStyle, marginBottom: '8px' }}>Recently Created</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {createdPlayers.map((p, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  backgroundColor: '#0f172a', borderRadius: '5px',
                  padding: '6px 10px', fontSize: '13px',
                }}>
                  <span style={{ fontSize: '10px', fontWeight: '700',
                    color: TIER_COLORS[p.tier] ?? '#94a3b8', letterSpacing: '0.02em', minWidth: '14px' }}>
                    {p.tier.replace('Tier', '')}
                  </span>
                  <span style={{ color: '#e2e8f0', flex: 1 }}>{p.name}</span>
                  <span style={{ color: '#64748b' }}>{p.position}</span>
                  <span style={{ color: '#94a3b8', minWidth: '36px', textAlign: 'right' }}>{p.rating}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>}

      {/* Grant Card */}
      {activeSection === 'cards' && <div style={sectionStyle}>
        <h2 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px' }}>Grant Card</h2>
        <p style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '16px' }}>
          Grant a trading card with specific edition, effect, and classification to a user.
        </p>

        <div style={{ marginBottom: '12px' }}>
          <div style={labelStyle}>User</div>
          <select value={cardUser?.id ?? ''} onChange={e => {
            const u = adminUsers.find(u => u.id === Number(e.target.value))
            setCardUser(u ?? null)
          }} style={selectStyle}>
            <option value="">Select user...</option>
            {adminUsers.map(u => (
              <option key={u.id} value={u.id}>
                {u.username || u.email} ({u.floobits}F)
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <div style={labelStyle}>Player <span style={{ color: '#64748b', fontWeight: '400' }}>(optional — random if empty)</span></div>
          {cardSelectedPlayer ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              backgroundColor: '#0f172a', borderRadius: '6px', padding: '8px 12px',
              border: '1px solid #334155',
            }}>
              <span style={{ color: '#e2e8f0', flex: 1 }}>
                {cardSelectedPlayer.name}
                <span style={{ color: '#94a3b8', marginLeft: '8px', fontSize: '12px' }}>
                  {cardSelectedPlayer.position} / {cardSelectedPlayer.teamName} / {cardSelectedPlayer.rating} OVR
                </span>
              </span>
              <button onClick={() => { setCardSelectedPlayer(null); setCardPlayerSearch(''); setCardEffect('') }}
                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '13px' }}>
                Clear
              </button>
            </div>
          ) : (
            <>
              <input value={cardPlayerSearch} onChange={e => setCardPlayerSearch(e.target.value)}
                style={inputStyle} placeholder="Search player name..." />
              {playerSearching && (
                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>Searching...</div>
              )}
              {cardPlayerResults.length > 0 && (
                <div style={{
                  marginTop: '4px', backgroundColor: '#0f172a', borderRadius: '6px',
                  border: '1px solid #334155', maxHeight: '200px', overflowY: 'auto',
                }}>
                  {cardPlayerResults.map(p => (
                    <button key={p.id} onClick={() => { setCardSelectedPlayer(p); setCardPlayerResults([]) }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                        padding: '8px 12px', background: 'none', border: 'none', borderBottom: '1px solid #1e293b',
                        color: '#e2e8f0', cursor: 'pointer', fontSize: '13px', textAlign: 'left',
                      }}>
                      <span style={{ color: '#94a3b8', fontSize: '11px', fontWeight: '700', width: '24px' }}>{p.position}</span>
                      <span style={{ flex: 1 }}>{p.name}</span>
                      <span style={{ color: '#94a3b8', fontSize: '12px' }}>{p.teamName}</span>
                      <span style={{ color: '#cbd5e1', fontSize: '12px', fontWeight: '600' }}>{p.rating}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
          <div>
            <div style={labelStyle}>Edition</div>
            <select value={cardEdition} onChange={e => { setCardEdition(e.target.value); setCardEffect('') }} style={selectStyle}>
              {(cardOptions?.editions ?? ['metallic', 'holographic', 'prismatic', 'diamond']).map(ed => (
                <option key={ed} value={ed} style={{ color: EDITION_COLORS_MAP[ed] }}>{ed.toUpperCase()}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={labelStyle}>Category</div>
            <select value={cardCategory} onChange={e => { setCardCategory(e.target.value); setCardEffect('') }} style={selectStyle}>
              <option value="">Default ({cardSelectedPlayer ? (CATEGORY_FOR_POSITION[cardSelectedPlayer.position] || '?') : '—'})</option>
              {(cardOptions?.categories ?? []).map(cat => (
                <option key={cat} value={cat}>{cat.replace(/_/g, ' ').toUpperCase()}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={labelStyle}>Effect</div>
            <select value={cardEffect} onChange={e => setCardEffect(e.target.value)} style={selectStyle}>
              <option value="">Random</option>
              {allEffects.map(ef => (
                <option key={ef.name} value={ef.name}>{ef.displayName}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={labelStyle}>Classification</div>
            <select value={cardClassification} onChange={e => setCardClassification(e.target.value)} style={selectStyle}>
              <option value="">None</option>
              {(cardOptions?.classifications ?? []).map(c => (
                <option key={c} value={c}>{c.toUpperCase().replace(/_/g, ' + ')}</option>
              ))}
            </select>
          </div>
        </div>

        <button onClick={handleGrantCard}
          disabled={cardLoading || !cardUser}
          style={{ ...btnStyle, opacity: cardLoading || !cardUser ? 0.5 : 1 }}>
          {cardLoading ? 'Granting...' : cardSelectedPlayer ? 'Grant Card' : 'Grant Random Card'}
        </button>
        {cardResult && <div style={{ marginTop: '10px', fontSize: '13px', color: '#22c55e' }}>{cardResult}</div>}
        {cardError && <div style={{ marginTop: '10px', fontSize: '13px', color: '#ef4444' }}>{cardError}</div>}
      </div>}

      {/* Grant Floobits */}
      {activeSection === 'floobits' && <div style={sectionStyle}>
        <h2 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px' }}>Grant Floobits</h2>
        <p style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '16px' }}>
          Add Floobits to a user's balance.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '12px' }}>
          <div>
            <div style={labelStyle}>User</div>
            <select value={floobitsUser?.id ?? ''} onChange={e => {
              const u = adminUsers.find(u => u.id === Number(e.target.value))
              setFloobitsUser(u ?? null)
            }} style={selectStyle}>
              <option value="">Select user...</option>
              {adminUsers.map(u => (
                <option key={u.id} value={u.id}>
                  {u.username || u.email} ({u.floobits}F)
                </option>
              ))}
            </select>
          </div>
          <div>
            <div style={labelStyle}>Amount</div>
            <input type="number" min={1} value={floobitsAmount}
              onChange={e => setFloobitsAmount(Math.max(1, Number(e.target.value)))}
              style={inputStyle} />
          </div>
        </div>
        <button onClick={handleGrantFloobits}
          disabled={floobitsLoading || !floobitsUser || floobitsAmount <= 0}
          style={{ ...btnStyle, opacity: floobitsLoading || !floobitsUser ? 0.5 : 1 }}>
          {floobitsLoading ? 'Granting...' : 'Grant Floobits'}
        </button>
        {floobitsResult && <div style={{ marginTop: '10px', fontSize: '13px', color: '#22c55e' }}>{floobitsResult}</div>}
        {floobitsError && <div style={{ marginTop: '10px', fontSize: '13px', color: '#ef4444' }}>{floobitsError}</div>}
      </div>}

      {/* Achievements */}
      {activeSection === 'achievements' && <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '700' }}>Achievements</h2>
          {achMetrics && (
            <div style={{ fontSize: '11px', color: '#94a3b8' }}>
              {achMetrics.totalUsers} users, Season {achMetrics.season || '—'} (per-season counts)
            </div>
          )}
        </div>
        {achLoading && <div style={{ fontSize: '13px', color: '#94a3b8' }}>Loading...</div>}
        {achError && <div style={{ fontSize: '13px', color: '#ef4444' }}>{achError}</div>}
        {achMetrics && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ color: '#94a3b8', textAlign: 'left' }}>
                <th style={{ padding: '6px 10px', borderBottom: '1px solid #334155', fontWeight: 600 }}>Achievement</th>
                <th style={{ padding: '6px 10px', borderBottom: '1px solid #334155', fontWeight: 600 }}>Category</th>
                <th style={{ padding: '6px 10px', borderBottom: '1px solid #334155', fontWeight: 600 }}>Scope</th>
                <th style={{ padding: '6px 10px', borderBottom: '1px solid #334155', fontWeight: 600, textAlign: 'right' }}>Target</th>
                <th style={{ padding: '6px 10px', borderBottom: '1px solid #334155', fontWeight: 600, textAlign: 'right' }}>Unlocks</th>
                <th style={{ padding: '6px 10px', borderBottom: '1px solid #334155', fontWeight: 600, textAlign: 'right' }}>%</th>
                <th style={{ padding: '6px 10px', borderBottom: '1px solid #334155', fontWeight: 600, textAlign: 'right' }}>Avg Progress</th>
              </tr>
            </thead>
            <tbody>
              {(achMetrics.achievements || []).map((a: any) => {
                const fmt = (n: number) => formatAchievementValue(a.key, n)
                return (
                  <tr key={a.id} style={{ borderBottom: '1px solid #1e293b' }}>
                    <td style={{ padding: '6px 10px', color: '#e2e8f0', fontWeight: 500 }}>{a.name}</td>
                    <td style={{ padding: '6px 10px', color: '#cbd5e1' }}>{a.category}</td>
                    <td style={{ padding: '6px 10px', color: '#cbd5e1' }}>{a.scope}</td>
                    <td style={{ padding: '6px 10px', color: '#cbd5e1', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(a.target)}</td>
                    <td style={{ padding: '6px 10px', color: '#e2e8f0', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{a.unlocks}</td>
                    <td style={{
                      padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                      color: a.unlockPct >= 50 ? '#22c55e' : a.unlockPct >= 20 ? '#f59e0b' : '#94a3b8',
                    }}>{a.unlockPct}%</td>
                    <td style={{ padding: '6px 10px', color: '#94a3b8', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(a.avgProgress)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>}

      {/* Registered Users */}
      {activeSection === 'users' && <div style={sectionStyle}>
        <h2 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px' }}>Registered Users</h2>

        {/* Controls row */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
          marginBottom: '16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600' }}>Filter:</span>
            {(['all', 'active', 'pending', 'inactive'] as const).map(f => (
              <button key={f} onClick={() => { setUserFilter(f); fetchUsers(userSort, f) }} style={{
                background: userFilter === f ? '#334155' : 'none',
                border: '1px solid', borderColor: userFilter === f ? '#475569' : '#334155',
                borderRadius: '4px', padding: '3px 10px', fontSize: '11px',
                fontWeight: '600', cursor: 'pointer',
                color: userFilter === f ? '#e2e8f0' : '#94a3b8',
              }}>
                {f === 'all' ? 'All' : f === 'active' ? 'Active' : f === 'pending' ? 'Pending' : 'Inactive'}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600' }}>Sort:</span>
            <select
              value={userSort}
              onChange={e => { setUserSort(e.target.value); fetchUsers(e.target.value, userFilter) }}
              style={{
                backgroundColor: '#0f172a', border: '1px solid #334155',
                borderRadius: '4px', padding: '3px 8px', fontSize: '11px',
                color: '#e2e8f0', cursor: 'pointer',
              }}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="last_login">Last Login</option>
              <option value="username">Username</option>
            </select>
          </div>
          <div style={{ flex: 1 }} />
          <button
            onClick={handleSendReminders}
            disabled={reminderLoading}
            style={{
              backgroundColor: '#334155', color: '#e2e8f0', border: 'none',
              borderRadius: '4px', padding: '4px 12px', fontSize: '11px',
              fontWeight: '600', cursor: reminderLoading ? 'not-allowed' : 'pointer',
              opacity: reminderLoading ? 0.5 : 1,
            }}
          >
            {reminderLoading ? 'Sending...' : 'Send Onboarding Reminders'}
          </button>
        </div>

        {reminderResult && (
          <div style={{
            fontSize: '12px', marginBottom: '12px', padding: '8px 12px',
            backgroundColor: '#0f172a', borderRadius: '4px',
            color: reminderResult.startsWith('Error') ? '#ef4444' : '#22c55e',
          }}>
            {reminderResult}
          </div>
        )}

        <p style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '12px' }}>
          {adminUsers.length} user{adminUsers.length !== 1 ? 's' : ''}
          {userFilter !== 'all' ? ` (${userFilter})` : ''}
        </p>
        {adminUsers.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#94a3b8' }}>No users found.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  {['Username', 'Email', 'Favorite Team', 'Floobits', 'Joined', 'Last Login', 'Status', 'Admin', ''].map(h => (
                    <th key={h} style={{
                      ...labelStyle, textAlign: 'left', padding: '8px 10px',
                      marginBottom: 0, whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {adminUsers.map(u => (
                  <tr key={u.id} style={{ borderBottom: '1px solid #1e293b' }}>
                    <td style={{ padding: '8px 10px', color: '#e2e8f0', fontWeight: '600', whiteSpace: 'nowrap' }}>
                      {u.username || <span style={{ color: '#64748b', fontStyle: 'italic' }}>—</span>}
                      <button
                        onClick={() => handleRerollUsername(u.id)}
                        disabled={rerollingUserId === u.id}
                        style={{
                          background: 'none', border: 'none',
                          color: rerollingUserId === u.id ? '#475569' : '#3b82f6',
                          cursor: rerollingUserId === u.id ? 'default' : 'pointer',
                          fontSize: '11px', fontWeight: '600',
                          marginLeft: '8px', padding: '2px 4px',
                        }}
                      >
                        {rerollingUserId === u.id ? '...' : 'Re-roll'}
                      </button>
                    </td>
                    <td style={{ padding: '8px 10px', color: '#cbd5e1' }}>{u.email}</td>
                    <td style={{ padding: '8px 10px', color: '#cbd5e1' }}>
                      {u.favoriteTeam || <span style={{ color: '#64748b' }}>—</span>}
                    </td>
                    <td style={{ padding: '8px 10px', color: '#cbd5e1', textAlign: 'right' }}>
                      {u.floobits.toLocaleString()}
                      <span style={{ color: '#64748b', fontSize: '11px', marginLeft: '4px' }}>
                        ({u.lifetimeEarned.toLocaleString()} / {u.lifetimeSpent.toLocaleString()})
                      </span>
                    </td>
                    <td style={{ padding: '8px 10px', color: '#94a3b8', whiteSpace: 'nowrap', fontSize: '12px' }}>
                      {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '8px 10px', color: '#94a3b8', whiteSpace: 'nowrap', fontSize: '12px' }}>
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <span style={{
                        fontSize: '11px', fontWeight: '600', letterSpacing: '0.02em',
                        color: !u.isActive ? '#ef4444' : u.onboarded ? '#22c55e' : '#f59e0b',
                      }}>
                        {!u.isActive ? 'Inactive' : u.onboarded ? 'Active' : 'Pending'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <button
                        onClick={() => handleToggleAdmin(u.id)}
                        disabled={togglingAdminId === u.id}
                        style={{
                          background: 'none', border: '1px solid',
                          borderColor: u.isAdmin ? '#22c55e' : '#334155',
                          borderRadius: '4px', padding: '2px 8px',
                          fontSize: '11px', fontWeight: '600',
                          color: u.isAdmin ? '#22c55e' : '#64748b',
                          cursor: togglingAdminId === u.id ? 'default' : 'pointer',
                        }}
                      >
                        {togglingAdminId === u.id ? '...' : u.isAdmin ? 'Admin' : 'User'}
                      </button>
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <button
                        onClick={() => handleDeleteUser(u)}
                        disabled={deletingUserId === u.id}
                        style={{
                          background: 'none', border: 'none',
                          color: deletingUserId === u.id ? '#475569' : '#ef4444',
                          cursor: deletingUserId === u.id ? 'default' : 'pointer',
                          fontSize: '11px', fontWeight: '600', padding: '2px 4px',
                        }}
                      >
                        {deletingUserId === u.id ? '...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>}

      {/* Settings */}
      {activeSection === 'rules' && <div style={sectionStyle}>
        <AdminRulesSection buildHeaders={buildHeaders} />
      </div>}

      {activeSection === 'settings' && <div style={sectionStyle}>
        <SeasonRolloverControl buildHeaders={buildHeaders} />
        <h2 style={{ fontSize: '16px', fontWeight: '700', margin: '0 0 16px 0' }}>App Settings</h2>
        <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '16px' }}>
          Runtime-editable settings. Changes apply on the next page load for any user (cached client-side per session).
        </p>

        {settingsError && (
          <div style={{ fontSize: '13px', color: '#ef4444', marginBottom: '12px' }}>{settingsError}</div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '560px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>
              Feedback URL
            </label>
            <input
              type="text"
              value={settingsForm.feedback_url}
              onChange={e => setSettingsForm(s => ({ ...s, feedback_url: e.target.value }))}
              disabled={settingsLoading}
              placeholder="https://forms.gle/..."
              style={{
                width: '100%', padding: '8px 10px',
                fontSize: '13px', color: '#e2e8f0',
                backgroundColor: '#0f172a',
                border: '1px solid #334155', borderRadius: '4px',
                fontFamily: 'inherit',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#cbd5e1', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={settingsForm.feedback_visible}
                onChange={e => setSettingsForm(s => ({ ...s, feedback_visible: e.target.checked }))}
                disabled={settingsLoading}
                style={{ width: '14px', height: '14px', cursor: 'pointer' }}
              />
              Show Feedback button in footer
            </label>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>
              Survey URL (mid-season modal)
            </label>
            <input
              type="text"
              value={settingsForm.survey_url}
              onChange={e => setSettingsForm(s => ({ ...s, survey_url: e.target.value }))}
              disabled={settingsLoading}
              placeholder="https://forms.gle/..."
              style={{
                width: '100%', padding: '8px 10px',
                fontSize: '13px', color: '#e2e8f0',
                backgroundColor: '#0f172a',
                border: '1px solid #334155', borderRadius: '4px',
                fontFamily: 'inherit',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#cbd5e1', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={settingsForm.survey_visible}
                onChange={e => setSettingsForm(s => ({ ...s, survey_visible: e.target.checked }))}
                disabled={settingsLoading}
                style={{ width: '14px', height: '14px', cursor: 'pointer' }}
              />
              Show survey modal this week
            </label>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>
              Survey modal text
            </label>
            <textarea
              value={settingsForm.survey_text}
              onChange={e => setSettingsForm(s => ({ ...s, survey_text: e.target.value }))}
              disabled={settingsLoading}
              placeholder="Leave empty to use the default copy."
              rows={5}
              style={{
                width: '100%', padding: '10px 12px',
                fontSize: '13px', color: '#e2e8f0',
                backgroundColor: '#0f172a',
                border: '1px solid #334155', borderRadius: '4px',
                fontFamily: 'inherit',
                resize: 'vertical' as const,
                lineHeight: 1.5,
              }}
            />
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
              Shown above the "Take the Survey" button. Newlines are preserved.
            </div>
          </div>

          <div style={{ paddingTop: '14px', borderTop: '1px solid #1e293b' }}>
            <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>
              Floos Bowl halftime show video
            </label>
            <input
              type="text"
              value={settingsForm.halftime_show_url}
              onChange={e => setSettingsForm(s => ({ ...s, halftime_show_url: e.target.value }))}
              disabled={settingsLoading}
              placeholder="https://www.youtube.com/watch?v=..."
              style={{
                width: '100%', padding: '8px 10px',
                fontSize: '13px', color: '#e2e8f0',
                backgroundColor: '#0f172a',
                border: '1px solid #334155', borderRadius: '4px',
                fontFamily: 'inherit',
              }}
            />
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
              YouTube link. Plays at Floos Bowl halftime. Leave empty to disable the show.
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>
              Halftime pause (seconds)
            </label>
            <input
              type="number"
              min={0}
              value={settingsForm.halftime_show_pause_seconds}
              onChange={e => setSettingsForm(s => ({ ...s, halftime_show_pause_seconds: e.target.value }))}
              disabled={settingsLoading}
              placeholder="120"
              style={{
                width: '140px', padding: '8px 10px',
                fontSize: '13px', color: '#e2e8f0',
                backgroundColor: '#0f172a',
                border: '1px solid #334155', borderRadius: '4px',
                fontFamily: 'inherit',
              }}
            />
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
              How long the Floos Bowl holds at halftime so the show can play. Scheduled mode only.
            </div>
          </div>

          <div style={{ paddingTop: '14px', borderTop: '1px solid #1e293b' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#e2e8f0', margin: '0 0 4px 0' }}>
              Anomaly System
            </h3>
            <p style={{ fontSize: '11px', color: '#94a3b8', margin: '0 0 12px 0' }}>
              Master controls for the in-game glitches, the Criticality event, and awakened powers.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#cbd5e1', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settingsForm.anomalies_enabled}
                  onChange={e => setSettingsForm(s => ({ ...s, anomalies_enabled: e.target.checked }))}
                  disabled={settingsLoading}
                  style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                />
                Anomalies (in-game glitches)
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#cbd5e1', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settingsForm.criticality_enabled}
                  onChange={e => setSettingsForm(s => ({ ...s, criticality_enabled: e.target.checked }))}
                  disabled={settingsLoading}
                  style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                />
                Criticality
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#cbd5e1', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settingsForm.awakened_powers_enabled}
                  onChange={e => setSettingsForm(s => ({ ...s, awakened_powers_enabled: e.target.checked }))}
                  disabled={settingsLoading}
                  style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                />
                Awakened powers
              </label>

              {/* Off hides every trade surface on the site; the trade records stay. */}
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#cbd5e1', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settingsForm.trading_enabled}
                  onChange={e => setSettingsForm(s => ({ ...s, trading_enabled: e.target.checked }))}
                  disabled={settingsLoading}
                  style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                />
                Trading
              </label>

              {/* Off: no Cores rule vote opens and an open one closes with no change, so the
                  league stays on standard rules. Criticality still randomizes each game's rules. */}
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#cbd5e1', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settingsForm.rule_votes_enabled}
                  onChange={e => setSettingsForm(s => ({ ...s, rule_votes_enabled: e.target.checked }))}
                  disabled={settingsLoading}
                  style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                />
                Rule votes
              </label>
              <div style={{ fontSize: '11px', color: '#94a3b8', margin: '-4px 0 0 22px' }}>
                Off keeps the league on its current rules (standard from the next season start). Criticality still randomizes rules per game.
              </div>
            </div>

            <div style={{ marginTop: '14px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '6px' }}>
                Intensity
              </label>
              <div style={{ display: 'flex', gap: '6px' }}>
                {(['low', 'normal', 'high', 'chaos'] as const).map(level => {
                  const selected = settingsForm.anomaly_intensity === level
                  const disabled = settingsLoading || !settingsForm.anomalies_enabled
                  return (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setSettingsForm(s => ({ ...s, anomaly_intensity: level }))}
                      disabled={disabled}
                      style={{
                        padding: '6px 14px',
                        fontSize: '12px', fontWeight: '600',
                        textTransform: 'capitalize' as const,
                        color: disabled ? '#64748b' : (selected ? '#ffffff' : '#cbd5e1'),
                        backgroundColor: selected ? '#3b82f6' : '#0f172a',
                        border: `1px solid ${selected ? '#3b82f6' : '#334155'}`,
                        borderRadius: '4px',
                        cursor: disabled ? 'default' : 'pointer',
                        opacity: disabled && !selected ? 0.5 : 1,
                        fontFamily: 'inherit',
                      }}
                    >
                      {level}
                    </button>
                  )
                })}
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>
                Glitch frequency (only applies when Anomalies are on).
              </div>
            </div>
          </div>

          <div style={{ paddingTop: '14px', borderTop: '1px solid #1e293b' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#e2e8f0', margin: '0 0 4px 0' }}>
              Awakened Powers
            </h3>
            <p style={{ fontSize: '11px', color: '#94a3b8', margin: '0 0 12px 0' }}>
              Charge tuning — lower "touches to charge" = the power fires more often.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {([
                ['QB', 'awakened_involve_qb'],
                ['RB', 'awakened_involve_rb'],
                ['WR', 'awakened_involve_wr'],
                ['TE', 'awakened_involve_te'],
                ['K', 'awakened_involve_k'],
              ] as const).map(([label, key]) => (
                <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '88px' }}>
                  <label style={{ fontSize: '12px', color: '#94a3b8' }}>{label}</label>
                  <input
                    type="number"
                    step="0.1"
                    min="1"
                    value={settingsForm[key]}
                    onChange={e => setSettingsForm(s => ({ ...s, [key]: e.target.value }))}
                    disabled={settingsLoading}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      fontSize: '13px', color: '#e2e8f0',
                      backgroundColor: '#0f172a',
                      border: '1px solid #334155', borderRadius: '4px',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box' as const,
                    }}
                  />
                </div>
              ))}
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>
              Touches per game needed to charge the power.
            </div>

            <div style={{ marginTop: '14px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '6px' }}>
                Defensive fire chance (%)
              </label>
              <input
                type="number"
                step="1"
                min="0"
                max="100"
                value={settingsForm.awakened_def_fire_chance}
                onChange={e => setSettingsForm(s => ({ ...s, awakened_def_fire_chance: e.target.value }))}
                disabled={settingsLoading}
                style={{
                  width: '120px',
                  padding: '8px 10px',
                  fontSize: '13px', color: '#e2e8f0',
                  backgroundColor: '#0f172a',
                  border: '1px solid #334155', borderRadius: '4px',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box' as const,
                }}
              />
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>
                lower = more weighted toward offense
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
            <button
              onClick={saveSettings}
              disabled={settingsLoading}
              style={{
                padding: '8px 16px',
                fontSize: '13px', fontWeight: '600',
                backgroundColor: settingsLoading ? '#334155' : '#3b82f6',
                color: '#ffffff',
                border: 'none', borderRadius: '4px',
                cursor: settingsLoading ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {settingsLoading ? 'Saving…' : 'Save'}
            </button>
            {settingsSaved && (
              <span style={{ fontSize: '12px', color: '#22c55e', fontWeight: '600' }}>{settingsSaved}</span>
            )}
          </div>
        </div>

        <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #1e293b', maxWidth: '560px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '700', margin: '0 0 8px 0', color: '#e2e8f0' }}>
            Personality Templates
          </h3>
          <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '12px', lineHeight: 1.5 }}>
            Hot-reload <code style={{ color: '#cbd5e1' }}>vibe_reactions.yaml</code>,{' '}
            <code style={{ color: '#cbd5e1' }}>quirk_reactions.yaml</code>, and{' '}
            <code style={{ color: '#cbd5e1' }}>player_flavor.yaml</code> from disk. Use after uploading
            new template files (e.g. via <code style={{ color: '#cbd5e1' }}>fly ssh sftp</code>) to apply
            changes without restarting.
          </p>
          {reloadError && (
            <div style={{ fontSize: '13px', color: '#ef4444', marginBottom: '10px' }}>{reloadError}</div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={reloadPersonalityTemplates}
              disabled={reloadLoading}
              style={{
                padding: '8px 16px',
                fontSize: '13px', fontWeight: '600',
                backgroundColor: reloadLoading ? '#334155' : '#3b82f6',
                color: '#ffffff',
                border: 'none', borderRadius: '4px',
                cursor: reloadLoading ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {reloadLoading ? 'Reloading…' : 'Reload YAML Files'}
            </button>
            {reloadResult && (
              <span style={{ fontSize: '12px', color: '#22c55e', fontWeight: '600' }}>{reloadResult}</span>
            )}
          </div>
        </div>
      </div>}
    </div>
    </div>
  )
}

// ── Main export ────────────────────────────────────────────────────────────

const AdminPage: React.FC = () => {
  const { user, getToken } = useAuth()
  const stored = sessionStorage.getItem(SESSION_KEY)
  const [password, setPassword] = useState<string | null>(stored)

  // Admin user: skip password gate, pass getToken so headers are built fresh per-request
  if (user?.isAdmin) {
    return <AdminContent password={null} getToken={getToken} />
  }

  // Fallback: password gate
  if (!password) {
    return <PasswordGate onAuth={setPassword} />
  }
  return <AdminContent password={password} getToken={null} />
}

export default AdminPage
